import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogOut, Shield, Users, Building2, TrendingUp, Upload, Plus, Pencil, Trash2, UserPlus, FileSpreadsheet, Download, Eye, ArrowLeft, Search, Wallet, Briefcase, Clock, AlertTriangle, CheckCircle, Palette, Calculator, CreditCard, ImageIcon, X, Check, Settings, Home, FileDown, Menu, KeyRound, BarChart3, Activity, Bell, QrCode } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer } from 'recharts';
import { QRCodeCanvas } from 'qrcode.react';
import PasswordConfirmModal from '@/components/PasswordConfirmModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatCurrency = (value) => { if (!value) return '₹0.00'; return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(value); };
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  if (!isNaN(dateStr) && Number(dateStr) > 10000) {
    const excelEpoch = new Date(1899, 11, 30);
    return new Date(excelEpoch.getTime() + Number(dateStr) * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
};
const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;

// Maturity status helper
const getMaturityStatus = (maturityDateStr) => {
  if (!maturityDateStr) return { status: 'unknown', daysRemaining: null, label: 'N/A' };
  let maturityDate;
  if (!isNaN(maturityDateStr) && Number(maturityDateStr) > 10000) {
    const excelEpoch = new Date(1899, 11, 30);
    maturityDate = new Date(excelEpoch.getTime() + Number(maturityDateStr) * 86400000);
  } else {
    maturityDate = new Date(maturityDateStr);
  }
  if (isNaN(maturityDate.getTime())) return { status: 'unknown', daysRemaining: null, label: 'N/A' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  maturityDate.setHours(0, 0, 0, 0);
  const diffTime = maturityDate - today;
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const formattedDate = maturityDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  if (daysRemaining < 0) return { status: 'matured', daysRemaining, label: formattedDate, tooltip: 'Already matured' };
  else if (daysRemaining < 30) return { status: 'critical', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  else if (daysRemaining < 60) return { status: 'warning', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  else if (daysRemaining < 90) return { status: 'upcoming', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  else return { status: 'safe', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
};

// Maturity Badge Component
const MaturityBadge = ({ maturityDate, maturityPct1, maturityDate2, maturityPct2, maturityDate3, maturityPct3 }) => {
  const hasMulti = maturityPct2 || maturityPct3;
  if (hasMulti) {
    const parts = [];
    const fmtDt = (d) => { try { const dt = new Date(d); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };
    if (maturityDate) parts.push(`${maturityPct1 || 100}% - ${fmtDt(maturityDate)}`);
    if (maturityPct2 && maturityDate2) parts.push(`${maturityPct2}% - ${fmtDt(maturityDate2)}`);
    if (maturityPct3 && maturityDate3) parts.push(`${maturityPct3}% - ${fmtDt(maturityDate3)}`);
    return <span className="text-xs leading-relaxed">{parts.join(', ')}</span>;
  }
  const { status, label, tooltip, daysRemaining } = getMaturityStatus(maturityDate);
  const statusStyles = {
    matured: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', icon: CheckCircle },
    critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle },
    warning: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: Clock },
    upcoming: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Clock },
    safe: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle },
    unknown: { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', icon: Clock }
  };
  const style = statusStyles[status];
  const Icon = style.icon;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`} title={tooltip}>
      <Icon className="h-3 w-3" /><span>{label}</span>
      {status === 'critical' && daysRemaining !== null && <span className="ml-1 px-1.5 py-0.5 bg-red-100 rounded text-[10px] font-bold">{daysRemaining}d</span>}
      {status === 'warning' && daysRemaining !== null && <span className="ml-1 px-1.5 py-0.5 bg-orange-100 rounded text-[10px] font-bold">{daysRemaining}d</span>}
    </div>
  );
};

// Preset color themes for quick selection
const THEME_PRESETS = [
  { name: 'Default Green', colors: { primary: '#1B4D3E', primary_hover: '#163d32', accent: '#D4AF37', light_bg: '#E8F5E9', card_bg: '#F8FAF9', sidebar_bg: '#FFFFFF', text_primary: '#1B4D3E', text_secondary: '#64748b' } },
  { name: 'Corporate Blue', colors: { primary: '#1E3A5F', primary_hover: '#152D4A', accent: '#3B82F6', light_bg: '#EFF6FF', card_bg: '#F0F7FF', sidebar_bg: '#FFFFFF', text_primary: '#1E3A5F', text_secondary: '#64748b' } },
  { name: 'Warm Gold', colors: { primary: '#78350F', primary_hover: '#5C2A0C', accent: '#D97706', light_bg: '#FFFBEB', card_bg: '#FFF8ED', sidebar_bg: '#FFFFFF', text_primary: '#78350F', text_secondary: '#78716c' } },
  { name: 'Modern Gray', colors: { primary: '#1F2937', primary_hover: '#111827', accent: '#6366F1', light_bg: '#F3F4F6', card_bg: '#F9FAFB', sidebar_bg: '#FFFFFF', text_primary: '#1F2937', text_secondary: '#6B7280' } },
  { name: 'Royal Purple', colors: { primary: '#4C1D95', primary_hover: '#3B1578', accent: '#A78BFA', light_bg: '#F5F3FF', card_bg: '#FAF5FF', sidebar_bg: '#FFFFFF', text_primary: '#4C1D95', text_secondary: '#7C7C8A' } },
  { name: 'Ocean Teal', colors: { primary: '#134E4A', primary_hover: '#0F3D39', accent: '#14B8A6', light_bg: '#F0FDFA', card_bg: '#F5FFFE', sidebar_bg: '#FFFFFF', text_primary: '#134E4A', text_secondary: '#64748b' } },
];

const AdminDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [redClientId, setRedClientId] = useState('');
  const [redInvestmentOptions, setRedInvestmentOptions] = useState([]);
  const [bondHoldingsData, setBondHoldingsData] = useState([]);
  const [bondHoldingsFilter, setBondHoldingsFilter] = useState('__all__');
  const [bondClientBreakdown, setBondClientBreakdown] = useState([]);
  const [loadingBondHoldings, setLoadingBondHoldings] = useState(false);
  const [bhTxnData, setBhTxnData] = useState({ investments: [], redemptions: [] });
  const [bhTxnClient, setBhTxnClient] = useState(null);
  const [showBhTxn, setShowBhTxn] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyPartnerCode, setNewKeyPartnerCode] = useState('');
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [apiKeysSummary, setApiKeysSummary] = useState({ total_keys: 0, active_keys: 0, inactive_keys: 0, total_requests: 0, requests_today: 0, requests_7d: 0, requests_30d: 0, unacknowledged_alerts: 0 });
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState(30);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [apiAlerts, setApiAlerts] = useState([]);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  // Password-confirmed delete pipeline
  const [pwdConfirm, setPwdConfirm] = useState({ open: false, title: '', description: '', itemLabel: '', onConfirmed: null });
  const requestDelete = useCallback(({ title = 'Confirm Delete', description, itemLabel, onConfirmed }) => {
    setPwdConfirm({ open: true, title, description: description || 'This action cannot be undone.', itemLabel, onConfirmed });
  }, []);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [qrPartnerName, setQrPartnerName] = useState('');
  const [qrLogoUrl, setQrLogoUrl] = useState('');
  const [qrShowLogo, setQrShowLogo] = useState(true);
  const [importFile, setImportFile] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Partner Dashboard View State
  const [viewingPartner, setViewingPartner] = useState(null);
  const [partnerClients, setPartnerClients] = useState([]);
  const [partnerSummary, setPartnerSummary] = useState({ total_clients: 0, total_invested_value: 0 });
  const [selectedPartnerClient, setSelectedPartnerClient] = useState(null);
  const [clientHoldings, setClientHoldings] = useState([]);
  const [clientSummary, setClientSummary] = useState(null);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [transactions, setTransactions] = useState({ investments: [], redemptions: [] });
  const [partnerStats, setPartnerStats] = useState({});
  
  // Color Theme State
  const [colorConfigs, setColorConfigs] = useState([]);
  const [showColorModal, setShowColorModal] = useState(false);
  const [editingColorConfig, setEditingColorConfig] = useState(null);
  const [colorFormData, setColorFormData] = useState({
    partner_code: '',
    primary: '#1B4D3E',
    primary_hover: '#163d32',
    accent: '#D4AF37',
    light_bg: '#E8F5E9',
    card_bg: '#F8FAF9',
    sidebar_bg: '#FFFFFF',
    text_primary: '#1B4D3E',
    text_secondary: '#64748b'
  });

  // Commission State
  const [commissionMonth, setCommissionMonth] = useState(new Date().getMonth() + 1);
  const [commissionYear, setCommissionYear] = useState(new Date().getFullYear());
  const [commissionPartner, setCommissionPartner] = useState('');
  const [commissionRows, setCommissionRows] = useState([]);
  const [commissionTotal, setCommissionTotal] = useState(0);
  const [commissionMeta, setCommissionMeta] = useState(null);
  const [computingCommission, setComputingCommission] = useState(false);

  // Payout State
  const [payoutMonth, setPayoutMonth] = useState(new Date().getMonth() + 1);
  const [payoutYear, setPayoutYear] = useState(new Date().getFullYear());
  const [payoutRows, setPayoutRows] = useState([]);
  const [payoutMeta, setPayoutMeta] = useState(null);
  const [generatingPayout, setGeneratingPayout] = useState(false);
  const [editingPayoutDate, setEditingPayoutDate] = useState(null);
  const [payoutDateValue, setPayoutDateValue] = useState('');

  // Bonds State
  const [bonds, setBonds] = useState([]);
  const [loadingBonds, setLoadingBonds] = useState(false);
  const [showBondModal, setShowBondModal] = useState(false);
  const [editingBond, setEditingBond] = useState(null);
  const [bondForm, setBondForm] = useState({ bond_isin: '', bond_name: '', rating: '', yield_rate: '', maturity_date: '', maturity_pct_1: '100', maturity_date_2: '', maturity_pct_2: '', maturity_date_3: '', maturity_pct_3: '', interest_payout_frequency: '', available: true, bond_image_url: '' });
  const [bondImageFile, setBondImageFile] = useState(null);
  const [uploadingBondImage, setUploadingBondImage] = useState(false);
  const [showBondImagePreview, setShowBondImagePreview] = useState(false);
  const [previewBondImage, setPreviewBondImage] = useState('');

  // Curated Bonds State
  const [curatedBonds, setCuratedBonds] = useState([]);
  const [loadingCurated, setLoadingCurated] = useState(false);

  // Partner Config State
  const [partnerConfigs, setPartnerConfigs] = useState([]);
  const [subpartners, setSubpartners] = useState([]);
  const [families, setFamilies] = useState([]);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [familyForm, setFamilyForm] = useState({ family_name: '', head_client_id: '', partner_code: '', member_client_ids: [] });
  const [cmrFile, setCmrFile] = useState(null);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ partner_code: '', allow_add_investment_redemption: false, allow_subpartner: false, allow_my_bonds: false, curated_bonds_source: 'only_all_bonds', hide_commission_from_subpartners: false, allow_my_bond_commission: false, hide_curated_from_clients: false });

  const calculatePartnerStats = useCallback(async () => {
    try {
      const [clientsRes, investmentsRes, redemptionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/investments`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/redemptions`, getAuthHeaders())
      ]);
      
      const clients = clientsRes.data;
      const investments = investmentsRes.data;
      const redemptions = redemptionsRes.data;
      
      const stats = {};
      
      // Group clients by partner
      clients.forEach(client => {
        if (client.partner_code) {
          if (!stats[client.partner_code]) {
            stats[client.partner_code] = { total_clients: 0, total_invested_value: 0 };
          }
          stats[client.partner_code].total_clients++;
        }
      });
      
      // Calculate invested value per client then aggregate by partner
      clients.forEach(client => {
        if (!client.partner_code) return;
        
        const clientInvestments = investments.filter(i => i.client_id === client.client_id);
        const clientRedemptions = redemptions.filter(r => r.client_id === client.client_id);
        
        // Calculate holdings
        const holdings = {};
        clientInvestments.forEach(inv => {
          const isin = inv.bond_isin;
          if (!holdings[isin]) holdings[isin] = { invested_qty: 0, redeemed_qty: 0, invested_value: 0 };
          holdings[isin].invested_qty += inv.quantity;
          holdings[isin].invested_value += inv.invested_value;
        });
        clientRedemptions.forEach(red => {
          const isin = red.bond_isin;
          if (holdings[isin]) holdings[isin].redeemed_qty += red.quantity;
        });
        
        let clientTotal = 0;
        Object.values(holdings).forEach(h => {
          const netQty = h.invested_qty - h.redeemed_qty;
          if (netQty > 0 && h.invested_qty > 0) {
            clientTotal += h.invested_value * (netQty / h.invested_qty);
          }
        });
        
        if (stats[client.partner_code]) {
          stats[client.partner_code].total_invested_value += clientTotal;
        }
      });
      
      setPartnerStats(stats);
    } catch (error) {
      /* partner stats calculation failed - non-critical */
    }
  }, [getAuthHeaders]);

  // Define fetch functions BEFORE fetchData to avoid "Cannot access before initialization" error
  const fetchBonds = useCallback(async () => {
    try {
      setLoadingBonds(true);
      const response = await axios.get(`${API_URL}/api/admin/bonds`, getAuthHeaders());
      setBonds(response.data || []);
    } catch (error) { toast.error('Failed to load bonds'); }
    finally { setLoadingBonds(false); }
  }, [getAuthHeaders]);

  const fetchRedInvestmentOptions = useCallback(async (clientId) => {
    if (!clientId) { setRedInvestmentOptions([]); return; }
    try {
      const response = await axios.get(`${API_URL}/api/partner/investment-options?client_id=${clientId}`, getAuthHeaders());
      setRedInvestmentOptions(response.data || []);
    } catch (error) { setRedInvestmentOptions([]); }
  }, [getAuthHeaders]);

  const fetchBondHoldingsReport = useCallback(async (bondIsin) => {
    try {
      setLoadingBondHoldings(true);
      const url = bondIsin ? `${API_URL}/api/bond-holdings-report?bond_isin=${encodeURIComponent(bondIsin)}` : `${API_URL}/api/bond-holdings-report`;
      const response = await axios.get(url, getAuthHeaders());
      if (response.data.type === 'bond_summary') {
        setBondHoldingsData(response.data.rows || []);
        setBondClientBreakdown([]);
      } else {
        setBondClientBreakdown(response.data.rows || []);
      }
    } catch (error) { console.error('Bond holdings error:', error?.response?.status, error?.message); toast.error('Failed to load bond holdings report'); }
    finally { setLoadingBondHoldings(false); }
  }, [getAuthHeaders]);

  const viewBhTransactions = async (row) => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/client/${row.client_id}/transactions/${encodeURIComponent(row.bond_isin)}`, getAuthHeaders());
      setBhTxnData(response.data);
      setBhTxnClient(row);
      setShowBhTxn(true);
    } catch (error) { toast.error('Failed to load transactions'); }
  };

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/api-keys`, getAuthHeaders());
      setApiKeys(response.data || []);
    } catch (error) { toast.error('Failed to load API keys'); }
  }, [getAuthHeaders]);

  const handleGenerateApiKey = async () => {
    if (!newKeyPartnerCode) { toast.error('Select a partner'); return; }
    try {
      const response = await axios.post(`${API_URL}/api/admin/api-keys`, { partner_code: newKeyPartnerCode, label: newKeyLabel || undefined }, getAuthHeaders());
      setGeneratedKey(response.data.api_key);
      fetchApiKeys();
      toast.success('API key generated');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate key'); }
  };

  const toggleApiKey = async (keyId) => {
    try {
      await axios.put(`${API_URL}/api/admin/api-keys/${keyId}/toggle`, {}, getAuthHeaders());
      fetchApiKeys();
    } catch (error) { toast.error('Failed to toggle key'); }
  };

  const deleteApiKey = (keyId, label) => {
    requestDelete({
      title: 'Delete API Key',
      description: 'Permanently delete this API key? The partner integration will stop working immediately.',
      itemLabel: label || keyId,
      onConfirmed: async () => {
        await axios.delete(`${API_URL}/api/admin/api-keys/${keyId}`, getAuthHeaders());
        fetchApiKeys();
        toast.success('API key deleted');
      },
    });
  };

  const fetchApiKeysSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/api-keys-summary`, getAuthHeaders());
      setApiKeysSummary(response.data || {});
    } catch (error) { /* silent — non-critical KPI fetch */ }
  }, [getAuthHeaders]);

  const openAnalytics = useCallback(async (keyId, days = 30) => {
    setLoadingAnalytics(true);
    setShowAnalyticsModal(true);
    setAnalyticsData(null);
    setAnalyticsRange(days);
    try {
      const response = await axios.get(`${API_URL}/api/admin/api-keys/${keyId}/usage?days=${days}`, getAuthHeaders());
      setAnalyticsData(response.data);
    } catch (error) {
      toast.error('Failed to load usage analytics');
      setShowAnalyticsModal(false);
    } finally { setLoadingAnalytics(false); }
  }, [getAuthHeaders]);

  const fetchApiAlerts = useCallback(async (onlyUnack = true) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/api-alerts?only_unacknowledged=${onlyUnack}`, getAuthHeaders());
      setApiAlerts(response.data || []);
    } catch (error) { /* silent */ }
  }, [getAuthHeaders]);

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API_URL}/api/admin/api-alerts/${alertId}/acknowledge`, {}, getAuthHeaders());
      toast.success('Alert acknowledged');
      fetchApiAlerts();
      fetchApiKeysSummary();
    } catch (error) { toast.error('Failed to acknowledge alert'); }
  };

  const acknowledgeAllAlerts = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/api-alerts/acknowledge-all`, {}, getAuthHeaders());
      toast.success(`${res.data.acknowledged_count} alert(s) acknowledged`);
      fetchApiAlerts();
      fetchApiKeysSummary();
    } catch (error) { toast.error('Failed to acknowledge alerts'); }
  };

  const fetchCuratedBonds = useCallback(async () => {
    try {
      setLoadingCurated(true);
      const response = await axios.get(`${API_URL}/api/curated-bonds`, getAuthHeaders());
      setCuratedBonds(response.data || []);
    } catch (error) { toast.error('Failed to load curated bonds'); }
    finally { setLoadingCurated(false); }
  }, [getAuthHeaders]);

  const fetchPartnerConfigs = useCallback(async () => {
    try {
      setLoadingConfigs(true);
      const response = await axios.get(`${API_URL}/api/admin/partner-configs`, getAuthHeaders());
      setPartnerConfigs(response.data || []);
    } catch (error) { toast.error('Failed to load partner configs'); }
    finally { setLoadingConfigs(false); }
  }, [getAuthHeaders]);

  const fetchData = useCallback(async (type) => {
    try {
      setLoading(true);
      if (type === 'themes') {
        const [colorsRes, partnersRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/partner-colors`, getAuthHeaders()),
          axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders())
        ]);
        setColorConfigs(colorsRes.data);
        setPartners(partnersRes.data);
        return;
      }
      if (type === 'commission') {
        const partnersRes = await axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders());
        setPartners(partnersRes.data);
        return;
      }
      if (type === 'payout') {
        return;
      }
      if (type === 'bonds') {
        fetchBonds();
        return;
      }
      if (type === 'curated-bonds') {
        fetchCuratedBonds();
        return;
      }
      if (type === 'bond-holdings') {
        if (bondHoldingsData.length === 0) fetchBondHoldingsReport();
        return;
      }
      if (type === 'api-keys') {
        fetchApiKeys();
        return;
      }
      if (type === 'config') {
        const partnersRes = await axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders());
        setPartners(partnersRes.data);
        fetchPartnerConfigs();
        return;
      }
      if (type === 'families') {
        const [famRes, partnersRes, clientsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/families`, getAuthHeaders()),
          partners.length ? Promise.resolve({ data: partners }) : axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders()),
          clients.length ? Promise.resolve({ data: clients }) : axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders())
        ]);
        setFamilies(famRes.data);
        setPartners(partnersRes.data);
        setClients(clientsRes.data);
        return;
      }
      const response = await axios.get(`${API_URL}/api/admin/${type}`, getAuthHeaders());
      switch (type) {
        case 'users': setUsers(response.data); break;
        case 'clients': setClients(response.data); break;
        case 'partners': 
          setPartners(response.data);
          calculatePartnerStats();
          break;
        case 'investments': setInvestments(response.data); break;
        case 'redemptions': setRedemptions(response.data); break;
        default: break;
      }
    } catch (error) { toast.error(`Failed to load ${type}`); }
    finally { setLoading(false); }
  // Note: clients, partners used conditionally in 'families' branch but their absence triggers API fetch fallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, fetchBonds, fetchCuratedBonds, fetchPartnerConfigs, calculatePartnerStats]);

  useEffect(() => { fetchData(activeTab); }, [activeTab, fetchData]);

  // Memoize sorted investments and redemptions to avoid re-sorting on every render
  const sortedInvestments = useMemo(() => [...investments].sort((a, b) => (b.transaction_date || '').localeCompare(a.transaction_date || '')), [investments]);
  const sortedRedemptions = useMemo(() => [...redemptions].sort((a, b) => (b.transaction_date || '').localeCompare(a.transaction_date || '')), [redemptions]);

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    if (type === 'user') {
      Promise.all([
        partners.length ? Promise.resolve({ data: partners }) : axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders()),
        clients.length ? Promise.resolve({ data: clients }) : axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/subpartners`, getAuthHeaders())
      ]).then(([pRes, cRes, spRes]) => { setPartners(pRes.data); setClients(cRes.data); setSubpartners(spRes.data); }).catch((err) => { console.error('Failed to load dropdown data:', err?.message); });
      setFormData(item ? { username: item.username, password: '', role: item.role, entity_id: item.entity_id || '' } : { username: '', password: '', role: 'client', entity_id: '' });
    }
    else if (type === 'client') {
      if (!partners.length) { axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders()).then(r => setPartners(r.data)).catch((err) => { console.error('Failed to load partners:', err?.message); }); }
      setCmrFile(null);
      setFormData(item ? { ...item } : { client_id: '', client_name: '', partner_code: '', partner_name: '', email: '', mobile: '' });
    }
    else if (type === 'partner') setFormData(item ? { ...item } : { partner_code: '', partner_name: '', partner_logo_url: '', partner_favicon_url: '', partner_web_url: '', partner_app_name: '', has_custom_login_page: false, email: '', mobile: '' });
    else if (type === 'investment') {
      if (!partners.length || !clients.length) {
        Promise.all([
          axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders()),
          axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders())
        ]).then(([pRes, cRes]) => { setPartners(pRes.data); setClients(cRes.data); }).catch(() => {});
      }
      if (item) {
        const toDD = (d) => { if (!d) return ''; const p = d.split(' ')[0].split('-'); return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0]}` : d; };
        const yld = item.yield_rate ? (item.yield_rate < 1 ? +(item.yield_rate * 100).toFixed(4) : item.yield_rate) : '';
        const ps = item.partner_share ? (item.partner_share < 1 ? +(item.partner_share * 100).toFixed(4) : item.partner_share) : '';
        setFormData({ ...item, transaction_date: toDD(item.transaction_date), maturity_date: toDD(item.maturity_date), maturity_pct_1: item.maturity_pct_1 || '100', maturity_date_2: toDD(item.maturity_date_2), maturity_pct_2: item.maturity_pct_2 || '', maturity_date_3: toDD(item.maturity_date_3), maturity_pct_3: item.maturity_pct_3 || '', yield_rate: yld, partner_share: ps, commn_type: item.commn_type || 'Trail' });
      } else {
        setFormData({ transaction_id: '', transaction_date: '', bond_isin: '', bond_name: '', quantity: '', face_value: '', invested_value: '', yield_rate: '', maturity_date: '', maturity_pct_1: '100', maturity_date_2: '', maturity_pct_2: '', maturity_date_3: '', maturity_pct_3: '', client_id: '', client_name: '', partner_code: '', partner_name: '', partner_share: '', commn_type: 'Trail' });
      }
    }
    else if (type === 'redemption') {
      if (!clients.length) { axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders()).then(r => setClients(r.data)).catch((err) => { console.error('Failed to load clients:', err?.message); }); }
      setRedClientId('');
      setRedInvestmentOptions([]);
      setFormData(item ? { ...item } : { investment_txn_id: '', transaction_date: '', quantity: '', redemption_value: '' });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      let endpoint = '', data = { ...formData };
      if (modalType === 'user') {
        endpoint = editingItem ? `/api/admin/users/${editingItem.id}` : '/api/admin/users';
        if (editingItem) await axios.put(`${API_URL}${endpoint}`, data, getAuthHeaders());
        else await axios.post(`${API_URL}${endpoint}`, data, getAuthHeaders());
        fetchData('users');
      } else if (modalType === 'client') {
        if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) { toast.error('Invalid email address'); return; }
        if (data.mobile && !/^\d{10}$/.test(data.mobile)) { toast.error('Mobile number must be 10 digits'); return; }
        if (!editingItem && data.client_id && clients.some(c => c.client_id === data.client_id)) { toast.error('Client ID already exists. Please use a unique ID.'); return; }
        if (!editingItem && !cmrFile) { toast.error('CMR (PDF) is required for new clients'); return; }
        // Remove cmr fields from the model payload (uploaded separately)
        const { cmr_path, cmr_filename, ...cleanData } = data;
        endpoint = editingItem ? `/api/admin/clients/${editingItem.client_id}` : '/api/admin/clients';
        if (editingItem) await axios.put(`${API_URL}${endpoint}`, cleanData, getAuthHeaders());
        else await axios.post(`${API_URL}${endpoint}`, cleanData, getAuthHeaders());
        // Upload CMR if file selected
        if (cmrFile) {
          const fd = new FormData();
          fd.append('file', cmrFile);
          await axios.post(`${API_URL}/api/upload-cmr/${data.client_id}`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
        }
        fetchData('clients');
      } else if (modalType === 'partner') {
        endpoint = editingItem ? `/api/admin/partners/${editingItem.partner_code}` : '/api/admin/partners';
        if (editingItem) {
          const codeChanged = data.partner_code && data.partner_code !== editingItem.partner_code;
          if (codeChanged && !window.confirm(`Rename Partner Code from "${editingItem.partner_code}" to "${data.partner_code}"?\n\nThis will update ALL linked records (clients, subpartners, investments, redemptions, commissions, payouts, themes, configs, API keys, user logins, etc.).\n\nThis cannot be undone. Continue?`)) return;
          const res = await axios.put(`${API_URL}${endpoint}`, data, getAuthHeaders());
          if (codeChanged && res.data?._rename_summary) {
            const total = Object.values(res.data._rename_summary).reduce((a, b) => a + (b || 0), 0);
            toast.success(`Partner Code renamed. ${total} linked records updated.`);
          }
          // After a code change, refresh all related lists in the dashboard
          if (codeChanged) { fetchData('clients'); fetchData('investments'); fetchData('redemptions'); }
        } else {
          await axios.post(`${API_URL}${endpoint}`, data, getAuthHeaders());
        }
        fetchData('partners');
      } else if (modalType === 'investment') {
        const toYMD = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 && p[2].length === 4 ? `${p[2]}-${p[1]}-${p[0]}` : d; };
        if (!editingItem && data.transaction_id && investments.some(inv => inv.transaction_id === data.transaction_id)) {
          toast.error('Transaction ID already exists. Please use a unique ID.'); return;
        }
        // Validate maturity % total
        const pct1 = Number(data.maturity_pct_1) || 100;
        if (pct1 < 100) {
          const total = pct1 + (Number(data.maturity_pct_2) || 0) + (Number(data.maturity_pct_3) || 0);
          if (total !== 100) { toast.error(`Maturity percentages must total 100% (currently ${total}%)`); return; }
        }
        data = { ...data, transaction_date: toYMD(data.transaction_date), maturity_date: toYMD(data.maturity_date), maturity_pct_1: data.maturity_pct_1 ? Number(data.maturity_pct_1) : null, maturity_date_2: toYMD(data.maturity_date_2) || null, maturity_pct_2: data.maturity_pct_2 ? Number(data.maturity_pct_2) : null, maturity_date_3: toYMD(data.maturity_date_3) || null, maturity_pct_3: data.maturity_pct_3 ? Number(data.maturity_pct_3) : null, quantity: Number(data.quantity), face_value: Number(data.face_value), invested_value: Number(data.invested_value), yield_rate: Number(data.yield_rate) / 100, partner_share: data.partner_share ? Number(data.partner_share) / 100 : null, commn_type: data.commn_type || 'Trail' };
        endpoint = editingItem ? `/api/admin/investments/${editingItem.transaction_id}` : '/api/admin/investments';
        if (editingItem) await axios.put(`${API_URL}${endpoint}`, data, getAuthHeaders());
        else await axios.post(`${API_URL}${endpoint}`, data, getAuthHeaders());
        fetchData('investments');
      } else if (modalType === 'redemption') {
        if (editingItem) {
          data = { ...data, quantity: Number(data.quantity), redemption_value: Number(data.redemption_value) };
          endpoint = `/api/admin/redemptions/${editingItem.transaction_id}`;
          await axios.put(`${API_URL}${endpoint}`, data, getAuthHeaders());
        } else {
          if (!data.investment_txn_id) { toast.error('Please select an investment transaction'); return; }
          if (!data.transaction_date || !data.quantity || !data.redemption_value) { toast.error('All fields are required'); return; }
          await axios.post(`${API_URL}/api/partner/redemptions`, { investment_txn_id: data.investment_txn_id, transaction_date: data.transaction_date, quantity: Number(data.quantity), redemption_value: Number(data.redemption_value) }, getAuthHeaders());
        }
        fetchData('redemptions');
      }
      toast.success(`${modalType} ${editingItem ? 'updated' : 'created'} successfully`);
      setShowModal(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const handleDelete = (type, id, label) => {
    const typeLabels = { users: 'User', clients: 'Client', partners: 'Partner', investments: 'Investment', redemptions: 'Redemption' };
    const friendly = typeLabels[type] || type;
    requestDelete({
      title: `Delete ${friendly}`,
      description: `Are you sure you want to delete this ${friendly.toLowerCase()}? This action cannot be undone.`,
      itemLabel: label || id,
      onConfirmed: async () => {
        await axios.delete(`${API_URL}/api/admin/${type}/${id}`, getAuthHeaders());
        toast.success(`${friendly} deleted`);
        fetchData(type === 'users' ? 'users' : type);
      },
    });
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Please select a file'); return; }
    setImporting(true);
    const formDataObj = new FormData();
    formDataObj.append('file', importFile);
    try {
      const response = await axios.post(`${API_URL}/api/admin/import-excel`, formDataObj, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported: ${response.data.imported.investments} investments, ${response.data.imported.redemptions} redemptions`);
      setShowImportModal(false);
      setImportFile(null);
      fetchData(activeTab);
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const updateForm = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  // Partner Dashboard View Functions
  const viewPartnerDashboard = async (partner) => {
    try {
      setLoading(true);
      // Fetch clients for this partner
      const clientsRes = await axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders());
      const partnerClientsData = clientsRes.data.filter(c => c.partner_code === partner.partner_code);
      
      // Calculate total invested for each client
      const investmentsRes = await axios.get(`${API_URL}/api/admin/investments`, getAuthHeaders());
      const redemptionsRes = await axios.get(`${API_URL}/api/admin/redemptions`, getAuthHeaders());
      
      const clientsWithValue = await Promise.all(partnerClientsData.map(async (client) => {
        const clientInvestments = investmentsRes.data.filter(i => i.client_id === client.client_id);
        const clientRedemptions = redemptionsRes.data.filter(r => r.client_id === client.client_id);
        
        // Calculate holdings
        const holdings = {};
        clientInvestments.forEach(inv => {
          const isin = inv.bond_isin;
          if (!holdings[isin]) holdings[isin] = { invested_qty: 0, redeemed_qty: 0, invested_value: 0 };
          holdings[isin].invested_qty += inv.quantity;
          holdings[isin].invested_value += inv.invested_value;
        });
        clientRedemptions.forEach(red => {
          const isin = red.bond_isin;
          if (holdings[isin]) holdings[isin].redeemed_qty += red.quantity;
        });
        
        let totalInvested = 0;
        Object.values(holdings).forEach(h => {
          const netQty = h.invested_qty - h.redeemed_qty;
          if (netQty > 0 && h.invested_qty > 0) {
            totalInvested += h.invested_value * (netQty / h.invested_qty);
          }
        });
        
        return { ...client, total_invested_value: totalInvested };
      }));
      
      const totalValue = clientsWithValue.reduce((sum, c) => sum + c.total_invested_value, 0);
      
      setPartnerClients(clientsWithValue);
      setPartnerSummary({ total_clients: clientsWithValue.length, total_invested_value: totalValue });
      setViewingPartner(partner);
    } catch (error) { 
      toast.error('Failed to load partner dashboard'); 
    }
    finally { setLoading(false); }
  };

  const viewPartnerClientPortfolio = async (client) => {
    try {
      setLoading(true);
      const investmentsRes = await axios.get(`${API_URL}/api/admin/investments`, getAuthHeaders());
      const redemptionsRes = await axios.get(`${API_URL}/api/admin/redemptions`, getAuthHeaders());
      
      const clientInvestments = investmentsRes.data.filter(i => i.client_id === client.client_id);
      const clientRedemptions = redemptionsRes.data.filter(r => r.client_id === client.client_id);
      
      // Calculate holdings
      const holdingsMap = {};
      clientInvestments.forEach(inv => {
        const isin = inv.bond_isin;
        if (!holdingsMap[isin]) {
          holdingsMap[isin] = {
            bond_isin: isin,
            bond_name: inv.bond_name,
            invested_qty: 0,
            redeemed_qty: 0,
            invested_value: 0,
            face_value: 0,
            yields: [],
            maturity_dates: [],
            maturity_schedule: null
          };
        }
        holdingsMap[isin].invested_qty += inv.quantity;
        holdingsMap[isin].invested_value += inv.invested_value;
        holdingsMap[isin].face_value += inv.face_value;
        holdingsMap[isin].yields.push(inv.yield_rate);
        if (inv.maturity_date) holdingsMap[isin].maturity_dates.push(inv.maturity_date);
        if ((inv.maturity_pct_2 || inv.maturity_pct_3) && !holdingsMap[isin].maturity_schedule) {
          holdingsMap[isin].maturity_schedule = { maturity_pct_1: inv.maturity_pct_1 || 100, maturity_date_2: inv.maturity_date_2, maturity_pct_2: inv.maturity_pct_2, maturity_date_3: inv.maturity_date_3, maturity_pct_3: inv.maturity_pct_3 };
        }
      });
      
      clientRedemptions.forEach(red => {
        const isin = red.bond_isin;
        if (holdingsMap[isin]) holdingsMap[isin].redeemed_qty += red.quantity;
      });
      
      const holdings = Object.values(holdingsMap).map(h => {
        const netQty = h.invested_qty - h.redeemed_qty;
        const ratio = h.invested_qty > 0 ? netQty / h.invested_qty : 0;
        return {
          bond_isin: h.bond_isin,
          bond_name: h.bond_name,
          net_quantity: netQty,
          invested_value: h.invested_value * ratio,
          face_value: h.face_value * ratio,
          avg_yield: h.yields.length > 0 ? h.yields.reduce((a, b) => a + b, 0) / h.yields.length : 0,
          maturity_date: h.maturity_dates.length > 0 ? h.maturity_dates.sort()[0] : null,
          ...(h.maturity_schedule || {})
        };
      }).filter(h => h.net_quantity > 0).sort((a, b) => a.bond_name.localeCompare(b.bond_name));
      
      const totalInvested = holdings.reduce((sum, h) => sum + h.invested_value, 0);
      const totalFace = holdings.reduce((sum, h) => sum + h.face_value, 0);
      
      setClientHoldings(holdings);
      setClientSummary({ total_invested_value: totalInvested, total_holdings: holdings.length, total_face_value: totalFace, client });
      setSelectedPartnerClient(client);
    } catch (error) { 
      toast.error('Failed to load client portfolio'); 
    }
    finally { setLoading(false); }
  };

  const viewHoldingTransactions = async (holding) => {
    try {
      const investmentsRes = await axios.get(`${API_URL}/api/admin/investments`, getAuthHeaders());
      const redemptionsRes = await axios.get(`${API_URL}/api/admin/redemptions`, getAuthHeaders());
      
      const invTxns = investmentsRes.data.filter(i => i.client_id === selectedPartnerClient.client_id && i.bond_isin === holding.bond_isin);
      const redTxns = redemptionsRes.data.filter(r => r.client_id === selectedPartnerClient.client_id && r.bond_isin === holding.bond_isin);
      
      setTransactions({ investments: invTxns, redemptions: redTxns });
      setSelectedHolding(holding);
      setShowTransactionsModal(true);
    } catch (error) { toast.error('Failed to load transactions'); }
  };

  const backToPartnerClients = () => {
    setSelectedPartnerClient(null);
    setClientHoldings([]);
    setClientSummary(null);
  };

  const backToPartners = () => {
    setViewingPartner(null);
    setPartnerClients([]);
    setPartnerSummary({ total_clients: 0, total_invested_value: 0 });
    setSelectedPartnerClient(null);
    setClientHoldings([]);
    setClientSummary(null);
  };

  // Color Theme CRUD
  const openColorEditor = (config = null) => {
    setEditingColorConfig(config);
    setColorFormData(config ? { ...config } : {
      partner_code: '',
      primary: '#1B4D3E',
      primary_hover: '#163d32',
      accent: '#D4AF37',
      light_bg: '#E8F5E9',
      card_bg: '#F8FAF9',
      sidebar_bg: '#FFFFFF',
      text_primary: '#1B4D3E',
      text_secondary: '#64748b'
    });
    setShowColorModal(true);
  };

  const saveColorConfig = async () => {
    if (!colorFormData.partner_code) { toast.error('Please select a partner'); return; }
    try {
      if (editingColorConfig) {
        await axios.put(`${API_URL}/api/admin/partner-colors/${colorFormData.partner_code}`, colorFormData, getAuthHeaders());
      } else {
        await axios.post(`${API_URL}/api/admin/partner-colors`, colorFormData, getAuthHeaders());
      }
      toast.success(`Color theme ${editingColorConfig ? 'updated' : 'created'} successfully`);
      setShowColorModal(false);
      fetchData('themes');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save color config'); }
  };

  const deleteColorConfig = (partnerCode) => {
    requestDelete({
      title: 'Delete Color Theme',
      description: `This partner will revert to default BondMaster colors.`,
      itemLabel: partnerCode,
      onConfirmed: async () => {
        await axios.delete(`${API_URL}/api/admin/partner-colors/${partnerCode}`, getAuthHeaders());
        toast.success('Color theme deleted');
        fetchData('themes');
      },
    });
  };

  const updateColor = (key, value) => setColorFormData(prev => ({ ...prev, [key]: value }));

  // Partners that don't have a color config yet
  const partnersWithoutTheme = useMemo(() => partners.filter(p => !colorConfigs.some(c => c.partner_code === p.partner_code)), [partners, colorConfigs]);

  // Bond functions (fetchBonds is defined earlier to avoid initialization order issues)
  const openBondModal = (bond = null) => {
    setEditingBond(bond);
    setBondImageFile(null);
    if (bond) {
      setBondForm({ bond_isin: bond.bond_isin, bond_name: bond.bond_name, rating: bond.rating || '', yield_rate: bond.yield_rate || '', maturity_date: bond.maturity_date || '', maturity_pct_1: bond.maturity_pct_1 || '100', maturity_date_2: bond.maturity_date_2 || '', maturity_pct_2: bond.maturity_pct_2 || '', maturity_date_3: bond.maturity_date_3 || '', maturity_pct_3: bond.maturity_pct_3 || '', interest_payout_frequency: bond.interest_payout_frequency || '', available: bond.available !== false, bond_image_url: bond.bond_image_url || '' });
    } else {
      setBondForm({ bond_isin: '', bond_name: '', rating: '', yield_rate: '', maturity_date: '', maturity_pct_1: '100', maturity_date_2: '', maturity_pct_2: '', maturity_date_3: '', maturity_pct_3: '', interest_payout_frequency: '', available: true, bond_image_url: '' });
    }
    setShowBondModal(true);
  };

  const handleBondImageUpload = async () => {
    if (!bondImageFile) return null;
    try {
      setUploadingBondImage(true);
      const formDataObj = new FormData();
      formDataObj.append('file', bondImageFile);
      const response = await axios.post(`${API_URL}/api/admin/bonds/upload-image`, formDataObj, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      return `${API_URL}${response.data.image_url}`;
    } catch (error) { toast.error('Failed to upload image'); return null; }
    finally { setUploadingBondImage(false); }
  };

  const handleSaveBond = async () => {
    if (!bondForm.bond_isin.trim()) { toast.error('Bond ISIN is required'); return; }
    if (!bondForm.bond_name.trim()) { toast.error('Bond Name is required'); return; }
    const bPct1 = Number(bondForm.maturity_pct_1) || 100;
    if (bPct1 < 100) {
      const bTotal = bPct1 + (Number(bondForm.maturity_pct_2) || 0) + (Number(bondForm.maturity_pct_3) || 0);
      if (bTotal !== 100) { toast.error(`Maturity percentages must total 100% (currently ${bTotal}%)`); return; }
    }
    try {
      let imageUrl = bondForm.bond_image_url;
      if (bondImageFile) {
        const uploadedUrl = await handleBondImageUpload();
        if (uploadedUrl) imageUrl = uploadedUrl;
      }
      const data = { ...bondForm, yield_rate: bondForm.yield_rate ? Number(bondForm.yield_rate) : null, maturity_pct_1: bondForm.maturity_pct_1 ? Number(bondForm.maturity_pct_1) : null, maturity_date_2: bondForm.maturity_date_2 || null, maturity_pct_2: bondForm.maturity_pct_2 ? Number(bondForm.maturity_pct_2) : null, maturity_date_3: bondForm.maturity_date_3 || null, maturity_pct_3: bondForm.maturity_pct_3 ? Number(bondForm.maturity_pct_3) : null, bond_image_url: imageUrl || null };
      if (editingBond) {
        await axios.put(`${API_URL}/api/admin/bonds/${encodeURIComponent(editingBond.bond_isin)}`, data, getAuthHeaders());
      } else {
        await axios.post(`${API_URL}/api/admin/bonds`, data, getAuthHeaders());
      }
      toast.success(`Bond ${editingBond ? 'updated' : 'created'} successfully`);
      setShowBondModal(false);
      fetchBonds();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save bond'); }
  };

  const handleDeleteBond = (bond_isin, label) => {
    requestDelete({
      title: 'Delete Bond',
      description: 'This will remove the bond from the master list. Existing investments referencing this bond remain.',
      itemLabel: label || bond_isin,
      onConfirmed: async () => {
        await axios.delete(`${API_URL}/api/admin/bonds/${encodeURIComponent(bond_isin)}`, getAuthHeaders());
        toast.success('Bond deleted');
        fetchBonds();
      },
    });
  };

  // fetchCuratedBonds and fetchPartnerConfigs are defined earlier to avoid initialization order issues
  const openConfigModal = (config = null) => {
    setEditingConfig(config);
    if (config) {
      setConfigForm({ partner_code: config.partner_code, allow_add_investment_redemption: config.allow_add_investment_redemption || false, allow_subpartner: config.allow_subpartner || false, allow_my_bonds: config.allow_my_bonds || false, curated_bonds_source: config.curated_bonds_source || 'only_all_bonds', hide_commission_from_subpartners: config.hide_commission_from_subpartners || false, allow_my_bond_commission: config.allow_my_bond_commission || false, hide_curated_from_clients: config.hide_curated_from_clients || false });
    } else {
      setConfigForm({ partner_code: '', allow_add_investment_redemption: false, allow_subpartner: false, allow_my_bonds: false, curated_bonds_source: 'only_all_bonds', hide_commission_from_subpartners: false, allow_my_bond_commission: false, hide_curated_from_clients: false });
    }
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!configForm.partner_code) { toast.error('Please select a partner'); return; }
    try {
      if (editingConfig) {
        await axios.put(`${API_URL}/api/admin/partner-configs/${encodeURIComponent(editingConfig.partner_code)}`, configForm, getAuthHeaders());
      } else {
        await axios.post(`${API_URL}/api/admin/partner-configs`, configForm, getAuthHeaders());
      }
      toast.success(`Config ${editingConfig ? 'updated' : 'created'} successfully`);
      setShowConfigModal(false);
      fetchPartnerConfigs();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save config'); }
  };

  const handleDeleteConfig = (partner_code) => {
    requestDelete({
      title: 'Delete Partner Config',
      description: 'Remove the partner-specific configuration overrides? The partner will revert to default behaviour.',
      itemLabel: partner_code,
      onConfirmed: async () => {
        await axios.delete(`${API_URL}/api/admin/partner-configs/${encodeURIComponent(partner_code)}`, getAuthHeaders());
        toast.success('Config removed');
        fetchPartnerConfigs();
      },
    });
  };

  // Commission functions
  const computeCommission = async () => {
    if (!commissionPartner) { toast.error('Please select a partner'); return; }
    try {
      setComputingCommission(true);
      const response = await axios.post(`${API_URL}/api/admin/compute-commission`, {
        month: commissionMonth,
        year: commissionYear,
        partner_code: commissionPartner
      }, getAuthHeaders());
      setCommissionRows(response.data.rows || []);
      setCommissionTotal(response.data.total_commission || 0);
      setCommissionMeta({ month_year: response.data.month_year, partner_name: response.data.partner_name, partner_code: response.data.partner_code });
      toast.success(response.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to compute commission'); }
    finally { setComputingCommission(false); }
  };

  const fetchCommissionReport = async () => {
    if (!commissionPartner) { toast.error('Please select a partner'); return; }
    try {
      setComputingCommission(true);
      const response = await axios.get(`${API_URL}/api/admin/commission-report?month=${commissionMonth}&year=${commissionYear}&partner_code=${commissionPartner}`, getAuthHeaders());
      setCommissionRows(response.data.rows || []);
      setCommissionTotal(response.data.total_commission || 0);
      setCommissionMeta({ month_year: response.data.month_year, partner_name: response.data.partner_name, partner_code: response.data.partner_code });
      if (response.data.rows?.length === 0) toast.info('No saved commission report found. Click "Compute" to generate.');
    } catch (error) { toast.error('Failed to fetch report'); }
    finally { setComputingCommission(false); }
  };

  const monthOptions = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  // Payout functions
  const generatePayout = async () => {
    try {
      setGeneratingPayout(true);
      const response = await axios.post(`${API_URL}/api/admin/generate-payout`, {
        month: payoutMonth, year: payoutYear
      }, getAuthHeaders());
      setPayoutRows(response.data.rows || []);
      setPayoutMeta({ month_year: response.data.month_year, month_name: response.data.month_name });
      toast.success(response.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate payout'); }
    finally { setGeneratingPayout(false); }
  };

  const fetchPayoutReport = async () => {
    try {
      setGeneratingPayout(true);
      const response = await axios.get(`${API_URL}/api/admin/payout-report?month=${payoutMonth}&year=${payoutYear}`, getAuthHeaders());
      setPayoutRows(response.data.rows || []);
      setPayoutMeta({ month_year: response.data.month_year, month_name: response.data.month_name });
      if (response.data.rows?.length === 0) toast.info('No payout data found. Click "Generate Payout" first.');
    } catch (error) { toast.error('Failed to fetch payout report'); }
    finally { setGeneratingPayout(false); }
  };

  const savePayoutDate = async (partnerCode) => {
    try {
      await axios.put(`${API_URL}/api/admin/payout-date`, {
        month: payoutMonth, year: payoutYear, partner_code: partnerCode, payout_date: payoutDateValue
      }, getAuthHeaders());
      setPayoutRows(prev => prev.map(r => r.partner_code === partnerCode ? { ...r, payout_date: payoutDateValue } : r));
      setEditingPayoutDate(null);
      setPayoutDateValue('');
      toast.success('Payout date updated');
    } catch (error) { toast.error('Failed to update payout date'); }
  };

  const formatPayoutDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr;
  };

  const adminNavItems = useMemo(() => [
    { key: 'users', label: 'Users', icon: UserPlus, testId: 'users-tab' },
    { key: 'clients', label: 'Clients', icon: Building2, testId: 'clients-tab' },
    { key: 'partners', label: 'Partners', icon: Users, testId: 'partners-tab' },
    { key: 'investments', label: 'Investments', icon: TrendingUp, testId: 'investments-tab' },
    { key: 'redemptions', label: 'Redemptions', icon: Download, testId: 'redemptions-tab' },
    { divider: true },
    { key: 'bonds', label: 'All Bonds', icon: Briefcase, testId: 'bonds-tab', onSelect: () => { if (bonds.length === 0) fetchBonds(); } },
    { key: 'curated-bonds', label: 'Curated Bonds', icon: CheckCircle, testId: 'curated-bonds-tab', onSelect: () => { if (curatedBonds.length === 0) fetchCuratedBonds(); } },
    { key: 'bond-holdings', label: 'Bond Holdings', icon: Briefcase, testId: 'bond-holdings-tab', onSelect: () => { if (bondHoldingsData.length === 0) fetchBondHoldingsReport(); } },
    { divider: true },
    { key: 'commission', label: 'Commission', icon: Calculator, testId: 'commission-tab' },
    { key: 'payout', label: 'Payout', icon: CreditCard, testId: 'payout-tab' },
    { divider: true },
    { key: 'themes', label: 'Themes', icon: Palette, testId: 'themes-tab' },
    { key: 'config', label: 'Config', icon: Settings, testId: 'config-tab', onSelect: () => { if (partnerConfigs.length === 0) fetchPartnerConfigs(); } },
    { key: 'families', label: 'Families', icon: Home, testId: 'families-tab', onSelect: () => { if (families.length === 0) fetchData('families'); } },
    { key: 'api-keys', label: 'API Keys', icon: KeyRound, testId: 'api-keys-tab', onSelect: () => { fetchApiKeys(); fetchApiKeysSummary(); fetchApiAlerts(); } },
  ], [bonds.length, curatedBonds.length, partnerConfigs.length, families.length, bondHoldingsData.length, fetchBonds, fetchCuratedBonds, fetchPartnerConfigs, fetchData, fetchBondHoldingsReport, fetchApiKeys, fetchApiKeysSummary, fetchApiAlerts]);

  const handleAdminNav = useCallback((item) => {
    setActiveTab(item.key);
    if (item.onSelect) item.onSelect();
    setSidebarOpen(false);
  }, []);

  return (
    <div className="dashboard-container" data-testid="admin-dashboard">
      {/* Mobile header */}
      <div className="mobile-header">
        <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        <img src="https://bondmaster.com.in/logoBond.png" alt="BondMaster" className="mobile-header-logo" />
        <div style={{ width: '2.25rem' }} />
      </div>

      {/* Sidebar overlay (mobile) */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo"><img src="https://bondmaster.com.in/logoBond.png" alt="BondMaster" className="h-10" /></div>
        <div className="sidebar-user">
          <div className="flex items-center gap-3 p-2.5 bg-[#E8F5E9] rounded-lg">
            <Shield className="h-5 w-5 text-[#1B4D3E]" />
            <div><p className="text-xs text-[#64748b]">Administrator</p><p className="font-medium text-[#1B4D3E] text-sm">{user.username}</p></div>
          </div>
          <Button className="w-full mt-2 bg-[#D4AF37] hover:bg-[#B8860B] text-white text-xs h-8" onClick={() => { setShowImportModal(true); setSidebarOpen(false); }} data-testid="import-excel-btn"><Upload className="h-3.5 w-3.5 mr-1.5" />Import Excel</Button>
        </div>
        <nav className="sidebar-nav" data-testid="admin-sidebar-nav">
          {adminNavItems.map((item, idx) =>
            item.divider ? <div key={`d-${idx}`} className="sidebar-nav-divider" /> : (
              <button
                key={item.key}
                className={`sidebar-nav-item ${activeTab === item.key ? 'active' : ''}`}
                style={{ '--nav-active-bg': '#1B4D3E' }}
                onClick={() => handleAdminNav(item)}
                data-testid={item.testId}
              >
                <item.icon className="nav-icon" />{item.label}
              </button>
            )
          )}
        </nav>
        <div className="sidebar-footer">
          <Button variant="outline" className="w-full justify-start text-[#64748b] hover:text-[#1B4D3E] hover:bg-[#E8F5E9] text-sm h-9" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Sign Out</Button>
        </div>
      </aside>
      <main className="main-content">
        <div className="mb-6"><h1 className="text-2xl md:text-3xl font-heading font-bold text-[#1B4D3E] mb-1">Admin Dashboard</h1><p className="text-[#64748b]">Manage users, clients, partners, and transactions</p></div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="hidden">
            {adminNavItems.filter(i => !i.divider).map(i => <TabsTrigger key={i.key} value={i.key}>{i.label}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="users">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b"><CardTitle className="text-lg font-heading text-[#1B4D3E]">User Management</CardTitle><Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => openModal('user')} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1" />Add User</Button></CardHeader>
              <CardContent className="p-0"><Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead>Username</TableHead><TableHead>Role</TableHead><TableHead>Entity ID</TableHead><TableHead>Entity Name</TableHead><TableHead className="text-center">Actions</TableHead></TableRow></TableHeader><TableBody>{users.map((u) => (<TableRow key={u.id}><TableCell className="font-medium">{u.username}</TableCell><TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${u.role === 'admin' ? 'bg-[#1B4D3E] text-white' : u.role === 'partner' ? 'bg-[#FFF9C4] text-[#B8860B]' : 'bg-[#E8F5E9] text-[#1B4D3E]'}`}>{u.role}</span></TableCell><TableCell className="font-mono text-sm">{u.entity_id || '-'}</TableCell><TableCell>{u.entity_name || '-'}</TableCell><TableCell className="text-center"><Button variant="ghost" size="sm" onClick={() => openModal('user', u)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('users', u.id, `${u.username} (${u.role})`)}><Trash2 className="h-4 w-4" /></Button></TableCell></TableRow>))}</TableBody></Table></CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="clients">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b"><CardTitle className="text-lg font-heading text-[#1B4D3E]">Client Management ({clients.length})</CardTitle><Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => openModal('client')} data-testid="add-client-btn"><Plus className="h-4 w-4 mr-1" />Add Client</Button></CardHeader>
              <CardContent className="p-0"><Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead>Client ID</TableHead><TableHead>Client Name</TableHead><TableHead>Partner Code</TableHead><TableHead>Partner Name</TableHead><TableHead className="text-center">CMR</TableHead><TableHead className="text-center">Actions</TableHead></TableRow></TableHeader><TableBody>{clients.map((c) => (<TableRow key={c.client_id}><TableCell className="font-mono text-sm">{c.client_id}</TableCell><TableCell className="font-medium">{c.client_name}</TableCell><TableCell>{c.partner_code || '-'}</TableCell><TableCell>{c.partner_name || '-'}</TableCell><TableCell className="text-center">{c.cmr_path ? <Button variant="ghost" size="sm" className="text-red-600" onClick={async () => { try { const res = await axios.get(`${API_URL}/api/download-cmr/${c.client_id}`, { ...getAuthHeaders(), responseType: 'blob' }); const url = window.URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = c.cmr_filename || 'cmr.pdf'; a.click(); window.URL.revokeObjectURL(url); } catch { toast.error('Failed to download CMR'); } }} data-testid={`download-cmr-${c.client_id}`}><FileDown className="h-4 w-4" /></Button> : <span className="text-gray-400 text-xs">-</span>}</TableCell><TableCell className="text-center"><Button variant="ghost" size="sm" onClick={() => openModal('client', c)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('clients', c.client_id, `${c.client_name} (${c.client_id})`)}><Trash2 className="h-4 w-4" /></Button></TableCell></TableRow>))}</TableBody></Table></CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="partners">
            {!viewingPartner ? (
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b"><CardTitle className="text-lg font-heading text-[#1B4D3E]">Partner Management ({partners.length})</CardTitle><Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => openModal('partner')} data-testid="add-partner-btn"><Plus className="h-4 w-4 mr-1" />Add Partner</Button></CardHeader>
                <CardContent className="p-0"><Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead>Partner Code</TableHead><TableHead>Partner Name</TableHead><TableHead>Email</TableHead><TableHead>Mobile</TableHead><TableHead className="text-center">Total Clients</TableHead><TableHead className="text-right">Total Invested Value</TableHead><TableHead className="text-center">Actions</TableHead></TableRow></TableHeader><TableBody>{partners.map((p) => (<TableRow key={p.partner_code}><TableCell className="font-mono text-sm">{p.partner_code}</TableCell><TableCell className="font-medium">{p.partner_name}</TableCell><TableCell className="text-sm">{p.email || <span className="text-gray-400">—</span>}</TableCell><TableCell className="text-sm font-mono">{p.mobile || <span className="text-gray-400">—</span>}</TableCell><TableCell className="text-center"><span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-[#E8F5E9] text-[#1B4D3E]">{partnerStats[p.partner_code]?.total_clients || 0}</span></TableCell><TableCell className="text-right font-semibold text-[#B8860B]">{formatCurrency(partnerStats[p.partner_code]?.total_invested_value || 0)}</TableCell><TableCell className="text-center"><Button variant="ghost" size="sm" className="text-[#1B4D3E]" onClick={() => viewPartnerDashboard(p)} title="View Partner Dashboard" data-testid={`view-partner-dashboard-${p.partner_code}`}><Eye className="h-4 w-4" /></Button><Button variant="ghost" size="sm" onClick={() => openModal('partner', p)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('partners', p.partner_code, `${p.partner_name} (${p.partner_code})`)}><Trash2 className="h-4 w-4" /></Button></TableCell></TableRow>))}</TableBody></Table></CardContent>
              </Card>
            ) : !selectedPartnerClient ? (
              <div>
                <Button variant="ghost" size="sm" onClick={backToPartners} className="mb-4 text-[#64748b] hover:text-[#1B4D3E]"><ArrowLeft className="h-4 w-4 mr-1" />Back to Partners</Button>
                <div className="mb-6">
                  <h2 className="text-xl font-heading font-bold text-[#1B4D3E] mb-1">{viewingPartner.partner_name}'s Dashboard</h2>
                  <p className="text-[#64748b]">Partner Code: {viewingPartner.partner_code}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Card className="border-0 shadow-sm"><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="p-3 bg-[#E8F5E9] rounded-lg"><Users className="h-6 w-6 text-[#1B4D3E]" /></div><div><p className="text-sm text-[#64748b]">Total Clients</p><p className="text-2xl font-bold text-[#1B4D3E]">{partnerSummary.total_clients}</p></div></div></CardContent></Card>
                  <Card className="border-0 shadow-sm"><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="p-3 bg-[#FFF9C4] rounded-lg"><Wallet className="h-6 w-6 text-[#B8860B]" /></div><div><p className="text-sm text-[#64748b]">Total Invested Value</p><p className="text-xl font-bold text-[#B8860B]">{formatCurrency(partnerSummary.total_invested_value)}</p></div></div></CardContent></Card>
                </div>
                <Card className="border-0 shadow-sm">
                  <CardHeader className="bg-[#F8FAF9] border-b"><CardTitle className="text-lg font-heading text-[#1B4D3E] flex items-center gap-2"><Building2 className="h-5 w-5" />Partner's Clients</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead>Client Name</TableHead><TableHead className="text-right">Total Invested Value</TableHead><TableHead className="text-center">Action</TableHead></TableRow></TableHeader>
                    <TableBody>{partnerClients.map((client) => (<TableRow key={client.client_id}><TableCell className="font-medium">{client.client_name}</TableCell><TableCell className="text-right font-semibold text-[#B8860B]">{formatCurrency(client.total_invested_value)}</TableCell><TableCell className="text-center"><Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => viewPartnerClientPortfolio(client)}><Search className="h-4 w-4 mr-2" />View Portfolio</Button></TableCell></TableRow>))}
                    {partnerClients.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-[#64748b]">No clients assigned to this partner</TableCell></TableRow>}</TableBody></Table>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div>
                <Button variant="ghost" size="sm" onClick={backToPartnerClients} className="mb-4 text-[#64748b] hover:text-[#1B4D3E]"><ArrowLeft className="h-4 w-4 mr-1" />Back to {viewingPartner.partner_name}'s Clients</Button>
                <div className="mb-6">
                  <h2 className="text-xl font-heading font-bold text-[#1B4D3E] mb-1">{selectedPartnerClient.client_name}</h2>
                  <p className="text-[#64748b]">Client ID: {selectedPartnerClient.client_id} | Partner: {viewingPartner.partner_name}</p>
                </div>
                {clientSummary && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <Card className="border-0 shadow-sm"><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="p-3 bg-[#E8F5E9] rounded-lg"><Wallet className="h-6 w-6 text-[#1B4D3E]" /></div><div><p className="text-sm text-[#64748b]">Total Invested Value</p><p className="text-2xl font-bold text-[#1B4D3E]">{formatCurrency(clientSummary.total_invested_value)}</p></div></div></CardContent></Card>
                    <Card className="border-0 shadow-sm"><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="p-3 bg-[#FFF9C4] rounded-lg"><Briefcase className="h-6 w-6 text-[#B8860B]" /></div><div><p className="text-sm text-[#64748b]">Active Holdings</p><p className="text-2xl font-bold text-[#B8860B]">{clientSummary.total_holdings}</p></div></div></CardContent></Card>
                  </div>
                )}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="bg-[#F8FAF9] border-b"><CardTitle className="text-lg font-heading text-[#1B4D3E]">Bond Portfolio Holdings</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead>Bond ISIN</TableHead><TableHead>Bond Name</TableHead><TableHead className="text-right">Net Quantity</TableHead><TableHead className="text-right">Invested Value</TableHead><TableHead className="text-center">Maturity Date</TableHead><TableHead className="text-center">Transactions</TableHead></TableRow></TableHeader>
                    <TableBody>{clientHoldings.map((holding) => (<TableRow key={holding.bond_isin}><TableCell className="font-mono text-sm">{holding.bond_isin}</TableCell><TableCell className="font-medium">{holding.bond_name}</TableCell><TableCell className="text-right font-semibold text-[#1B4D3E]">{holding.net_quantity}</TableCell><TableCell className="text-right font-semibold text-[#B8860B]">{formatCurrency(holding.invested_value)}</TableCell><TableCell className="text-center"><MaturityBadge maturityDate={holding.maturity_date} maturityPct1={holding.maturity_pct_1} maturityDate2={holding.maturity_date_2} maturityPct2={holding.maturity_pct_2} maturityDate3={holding.maturity_date_3} maturityPct3={holding.maturity_pct_3} /></TableCell><TableCell className="text-center"><button className="lens-btn" onClick={() => viewHoldingTransactions(holding)}><Search className="h-4 w-4" /></button></TableCell></TableRow>))}
                    {clientHoldings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#64748b]">No active holdings</TableCell></TableRow>}</TableBody></Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
          <TabsContent value="investments">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b"><CardTitle className="text-lg font-heading text-[#1B4D3E]">Investments ({investments.length})</CardTitle><Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => openModal('investment')} data-testid="add-investment-btn"><Plus className="h-4 w-4 mr-1" />Add Investment</Button></CardHeader>
              <CardContent className="p-0 overflow-x-auto"><Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead>Transaction Date</TableHead><TableHead>Bond ISIN</TableHead><TableHead>Bond Name</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Invested Value</TableHead><TableHead className="text-center">Actions</TableHead></TableRow></TableHeader><TableBody>{sortedInvestments.slice(0, 100).map((inv) => (<TableRow key={inv.transaction_id}><TableCell className="text-xs">{inv.transaction_date?.split(' ')[0] || '-'}</TableCell><TableCell className="font-mono text-xs">{inv.bond_isin}</TableCell><TableCell className="max-w-[200px] truncate">{inv.bond_name}</TableCell><TableCell>{inv.client_name}</TableCell><TableCell className="text-right">{inv.quantity}</TableCell><TableCell className="text-right font-semibold text-[#B8860B]">{formatCurrency(inv.invested_value)}</TableCell><TableCell className="text-center"><Button variant="ghost" size="sm" onClick={() => openModal('investment', inv)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('investments', inv.transaction_id, `${inv.bond_name} · ${inv.client_name} · ${inv.transaction_date?.split(' ')[0] || ''}`)}><Trash2 className="h-4 w-4" /></Button></TableCell></TableRow>))}</TableBody></Table>{investments.length > 100 && <p className="text-center py-4 text-[#64748b] text-sm">Showing first 100 of {investments.length} records</p>}</CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="redemptions">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between bg-[#FFF9C4]/30 border-b"><CardTitle className="text-lg font-heading text-[#B8860B]">Redemptions ({redemptions.length})</CardTitle><Button size="sm" className="bg-[#D4AF37] hover:bg-[#B8860B]" onClick={() => openModal('redemption')} data-testid="add-redemption-btn"><Plus className="h-4 w-4 mr-1" />Add Redemption</Button></CardHeader>
              <CardContent className="p-0 overflow-x-auto"><Table><TableHeader><TableRow className="bg-[#FFF9C4]/30"><TableHead>Transaction Date</TableHead><TableHead>Bond ISIN</TableHead><TableHead>Bond Name</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Redemption Value</TableHead><TableHead className="text-center">Actions</TableHead></TableRow></TableHeader><TableBody>{sortedRedemptions.slice(0, 100).map((red, idx) => (<TableRow key={`${red.transaction_id}-${idx}`}><TableCell className="text-xs">{red.transaction_date?.split(' ')[0] || '-'}</TableCell><TableCell className="font-mono text-xs">{red.bond_isin}</TableCell><TableCell className="max-w-[200px] truncate">{red.bond_name}</TableCell><TableCell>{red.client_name}</TableCell><TableCell className="text-right">{red.quantity}</TableCell><TableCell className="text-right font-semibold text-[#B8860B]">{formatCurrency(red.redemption_value)}</TableCell><TableCell className="text-center"><Button variant="ghost" size="sm" onClick={() => openModal('redemption', red)}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete('redemptions', red.transaction_id, `${red.bond_name} · ${red.client_name} · ${red.transaction_date?.split(' ')[0] || ''}`)}><Trash2 className="h-4 w-4" /></Button></TableCell></TableRow>))}</TableBody></Table>{redemptions.length > 100 && <p className="text-center py-4 text-[#64748b] text-sm">Showing first 100 of {redemptions.length} records</p>}</CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="themes">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b">
                <CardTitle className="text-lg font-heading text-[#1B4D3E] flex items-center gap-2"><Palette className="h-5 w-5" />Partner Color Themes ({colorConfigs.length})</CardTitle>
                <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => openColorEditor()} disabled={partnersWithoutTheme.length === 0} data-testid="add-theme-btn"><Plus className="h-4 w-4 mr-1" />Add Theme</Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#E8F5E9]/50">
                      <TableHead className="text-[#1B4D3E] font-semibold">Partner</TableHead>
                      <TableHead className="text-[#1B4D3E] font-semibold text-center">Colors</TableHead>
                      <TableHead className="text-[#1B4D3E] font-semibold text-center">Preview</TableHead>
                      <TableHead className="text-[#1B4D3E] font-semibold text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {colorConfigs.map((config) => {
                      const partner = partners.find(p => p.partner_code === config.partner_code);
                      return (
                        <TableRow key={config.partner_code}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{partner?.partner_name || config.partner_code}</p>
                              <p className="text-xs text-[#64748b] font-mono">{config.partner_code}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1.5">
                              {[config.primary, config.accent, config.light_bg, config.card_bg].map((color, i) => (
                                <div key={color} className="w-6 h-6 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: color }} title={color} />
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-2">
                              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: config.primary }}>Button</div>
                              <div className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: config.light_bg, color: config.accent }}>{formatCurrency(12345)}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" onClick={() => openColorEditor(config)} data-testid={`edit-theme-${config.partner_code}`}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteColorConfig(config.partner_code)} data-testid={`delete-theme-${config.partner_code}`}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {colorConfigs.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-[#64748b]">No custom themes configured. Partners use the default green/gold theme.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            {partnersWithoutTheme.length > 0 && colorConfigs.length > 0 && (
              <p className="mt-3 text-sm text-[#64748b]">{partnersWithoutTheme.length} partner{partnersWithoutTheme.length > 1 ? 's' : ''} using default theme: {partnersWithoutTheme.map(p => p.partner_name).join(', ')}</p>
            )}
          </TabsContent>
          {/* All Bonds Tab */}
          <TabsContent value="bonds">
            <Card className="border-0 shadow-sm" data-testid="bonds-list">
              <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b">
                <CardTitle className="text-lg font-heading text-[#1B4D3E] flex items-center gap-2"><Briefcase className="h-5 w-5" />All Bonds</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-[#1B4D3E] border-[#1B4D3E]" onClick={fetchBonds} disabled={loadingBonds} data-testid="refresh-bonds-btn">
                    {loadingBonds ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}Refresh
                  </Button>
                  <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => openBondModal()} data-testid="add-bond-btn"><Plus className="h-4 w-4 mr-1" />Add Bond</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#E8F5E9]/50">
                      <TableHead className="font-semibold text-[#1B4D3E]">Bond ISIN</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Name</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Rating</TableHead>
                      <TableHead className="font-semibold text-right text-[#1B4D3E]">Yield</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Maturity</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Int. Payout Freq.</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Available</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Image</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bonds.map((bond, idx) => (
                      <TableRow key={bond.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                        <TableCell className="font-mono text-sm">{bond.bond_isin}</TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate" title={bond.bond_name}>{bond.bond_name}</TableCell>
                        <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#E8F5E9] text-[#1B4D3E]">{bond.rating || '-'}</span></TableCell>
                        <TableCell className="text-right font-semibold text-[#D4AF37]">{bond.yield_rate ? `${bond.yield_rate}%` : '-'}</TableCell>
                        <TableCell>{(bond.maturity_pct_2 || bond.maturity_pct_3) ? <MaturityBadge maturityDate={bond.maturity_date} maturityPct1={bond.maturity_pct_1} maturityDate2={bond.maturity_date_2} maturityPct2={bond.maturity_pct_2} maturityDate3={bond.maturity_date_3} maturityPct3={bond.maturity_pct_3} /> : (bond.maturity_date ? formatDate(bond.maturity_date) : '-')}</TableCell>
                        <TableCell>{bond.interest_payout_frequency || '-'}</TableCell>
                        <TableCell className="text-center">{bond.available ? <Check className="h-4 w-4 text-green-600 inline" /> : <X className="h-4 w-4 text-red-500 inline" />}</TableCell>
                        <TableCell className="text-center">
                          {bond.bond_image_url ? (
                            <Button variant="ghost" size="sm" onClick={() => { setPreviewBondImage(bond.bond_image_url); setShowBondImagePreview(true); }} data-testid={`view-bond-image-${bond.bond_isin}`}>
                              <ImageIcon className="h-4 w-4 text-[#1B4D3E]" />
                            </Button>
                          ) : <span className="text-gray-400 text-xs">—</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="sm" onClick={() => openBondModal(bond)} data-testid={`edit-bond-${bond.bond_isin}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteBond(bond.bond_isin)} data-testid={`delete-bond-${bond.bond_isin}`}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {bonds.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#64748b]">{loadingBonds ? 'Loading...' : 'No bonds added yet. Click "Add Bond" to get started.'}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          {/* Curated Bonds Tab */}
          <TabsContent value="curated-bonds">
            <Card className="border-0 shadow-sm" data-testid="curated-bonds-list">
              <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b">
                <CardTitle className="text-lg font-heading text-[#1B4D3E] flex items-center gap-2"><CheckCircle className="h-5 w-5 text-[#D4AF37]" />Curated Bonds</CardTitle>
                <Button variant="outline" size="sm" className="text-[#1B4D3E] border-[#1B4D3E]" onClick={fetchCuratedBonds} disabled={loadingCurated} data-testid="refresh-curated-bonds-btn">
                  {loadingCurated ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}Refresh
                </Button>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#E8F5E9]/50">
                      <TableHead className="font-semibold text-[#1B4D3E]">Bond ISIN</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Name</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Rating</TableHead>
                      <TableHead className="font-semibold text-right text-[#1B4D3E]">Yield</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Maturity</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Int. Payout Freq.</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Image</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {curatedBonds.map((bond, idx) => (
                      <TableRow key={bond.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                        <TableCell className="font-mono text-sm">{bond.bond_isin}</TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate" title={bond.bond_name}>{bond.bond_name}</TableCell>
                        <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#E8F5E9] text-[#1B4D3E]">{bond.rating || '-'}</span></TableCell>
                        <TableCell className="text-right font-semibold text-[#D4AF37]">{bond.yield_rate ? `${bond.yield_rate}%` : '-'}</TableCell>
                        <TableCell>{(bond.maturity_pct_2 || bond.maturity_pct_3) ? <MaturityBadge maturityDate={bond.maturity_date} maturityPct1={bond.maturity_pct_1} maturityDate2={bond.maturity_date_2} maturityPct2={bond.maturity_pct_2} maturityDate3={bond.maturity_date_3} maturityPct3={bond.maturity_pct_3} /> : (bond.maturity_date ? formatDate(bond.maturity_date) : '-')}</TableCell>
                        <TableCell>{bond.interest_payout_frequency || '-'}</TableCell>
                        <TableCell className="text-center">
                          {bond.bond_image_url ? (
                            <Button variant="ghost" size="sm" onClick={() => { setPreviewBondImage(bond.bond_image_url); setShowBondImagePreview(true); }} data-testid={`curated-view-image-${bond.bond_isin}`}>
                              <ImageIcon className="h-4 w-4 text-[#1B4D3E]" />
                            </Button>
                          ) : <span className="text-gray-400 text-xs">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                    {curatedBonds.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#64748b]">{loadingCurated ? 'Loading...' : 'No curated bonds available'}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          {/* Commission Tab */}
          <TabsContent value="commission">
            <Card className="border-0 shadow-sm mb-6">
              <CardHeader className="bg-[#F8FAF9] border-b">
                <CardTitle className="text-lg font-heading text-[#1B4D3E] flex items-center gap-2"><Calculator className="h-5 w-5" />Partner Commission Computation</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-44">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Month</label>
                    <Select value={String(commissionMonth)} onValueChange={(v) => setCommissionMonth(Number(v))}>
                      <SelectTrigger data-testid="commission-month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-28">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Year</label>
                    <Input type="number" value={commissionYear} onChange={(e) => setCommissionYear(Number(e.target.value))} min={2000} max={2099} data-testid="commission-year-input" />
                  </div>
                  <div className="w-64">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Partner</label>
                    <Select value={commissionPartner} onValueChange={setCommissionPartner}>
                      <SelectTrigger data-testid="commission-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                      <SelectContent>
                        {partners.map(p => <SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={computeCommission} disabled={computingCommission || !commissionPartner} data-testid="compute-commission-btn">
                    {computingCommission ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Calculator className="h-4 w-4 mr-2" />}
                    Compute
                  </Button>
                  <Button variant="outline" className="border-[#1B4D3E] text-[#1B4D3E]" onClick={fetchCommissionReport} disabled={computingCommission || !commissionPartner} data-testid="fetch-commission-btn">
                    <Search className="h-4 w-4 mr-2" />View Saved
                  </Button>
                </div>
              </CardContent>
            </Card>
            {commissionMeta && (
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-[#E8F5E9]/50 border-b">
                  <div>
                    <CardTitle className="text-lg font-heading text-[#1B4D3E]">Commission Report: {commissionMeta.month_year}</CardTitle>
                    <p className="text-sm text-[#64748b] mt-1">Partner: {commissionMeta.partner_name} ({commissionMeta.partner_code})</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#64748b]">Total Commission</p>
                    <p className="text-2xl font-bold text-[#1B4D3E]">{formatCurrency(commissionTotal)}</p>
                  </div>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#E8F5E9]/30">
                        <TableHead className="text-[#1B4D3E] font-semibold">Client ID</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold">Client Name</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold">Txn Date</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold">Bond Name</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold">Type</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-right">Net Qty</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-right">Net Face Value</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-center">Days Held</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-center">Partner Share</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-right">Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissionRows.map((row, idx) => (
                        <TableRow key={`${row.client_id}-${row.bond_isin}-${row.type}`} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                          <TableCell className="font-mono text-xs">{row.client_id}</TableCell>
                          <TableCell className="font-medium">{row.client_name}</TableCell>
                          <TableCell>{formatDate(row.investment_date)}</TableCell>
                          <TableCell className="max-w-[200px] truncate" title={row.bond_name}>{row.bond_name}</TableCell>
                          <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.type === 'Holding' ? 'bg-[#E8F5E9] text-[#1B4D3E]' : 'bg-[#FFF9C4] text-[#B8860B]'}`}>{row.type}</span></TableCell>
                          <TableCell className="text-right">{row.net_quantity}</TableCell>
                          <TableCell className="text-right font-semibold text-[#1B4D3E]">{formatCurrency(row.net_face_value)}</TableCell>
                          <TableCell className="text-center">{row.days_held}</TableCell>
                          <TableCell className="text-center">{((row.partner_share || 0) * 100).toFixed(2)}%</TableCell>
                          <TableCell className="text-right font-semibold text-[#B8860B]">{formatCurrency(row.commission_amount)}</TableCell>
                        </TableRow>
                      ))}
                      {commissionRows.length === 0 && (
                        <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#64748b]">No commission data. Select a partner and month, then click "Compute".</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          {/* Payout Tab */}
          <TabsContent value="payout">
            <Card className="border-0 shadow-sm mb-6">
              <CardHeader className="bg-[#F8FAF9] border-b">
                <CardTitle className="text-lg font-heading text-[#1B4D3E] flex items-center gap-2"><CreditCard className="h-5 w-5" />Commission Payout Report</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-44">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Month</label>
                    <Select value={String(payoutMonth)} onValueChange={(v) => setPayoutMonth(Number(v))}>
                      <SelectTrigger data-testid="payout-month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-28">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Year</label>
                    <Input type="number" value={payoutYear} onChange={(e) => setPayoutYear(Number(e.target.value))} min={2000} max={2099} data-testid="payout-year-input" />
                  </div>
                  <Button className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={generatePayout} disabled={generatingPayout} data-testid="generate-payout-btn">
                    {generatingPayout ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <CreditCard className="h-4 w-4 mr-2" />}
                    Generate Payout
                  </Button>
                  <Button variant="outline" className="border-[#1B4D3E] text-[#1B4D3E]" onClick={fetchPayoutReport} disabled={generatingPayout} data-testid="fetch-payout-btn">
                    <Search className="h-4 w-4 mr-2" />View Saved
                  </Button>
                </div>
              </CardContent>
            </Card>
            {payoutMeta && (
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-[#E8F5E9]/50 border-b">
                  <CardTitle className="text-lg font-heading text-[#1B4D3E]">Payout Report: {payoutMeta.month_name} {payoutYear}</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#E8F5E9]/30">
                        <TableHead className="text-[#1B4D3E] font-semibold">Month-Year</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold">Partner ID</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold">Partner Name</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-right">Total Commission</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-right">TDS</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-right">Net Payout</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-center">Payout Date</TableHead>
                        <TableHead className="text-[#1B4D3E] font-semibold text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payoutRows.map((row, idx) => (
                        <TableRow key={row.partner_code} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}>
                          <TableCell>{payoutMeta.month_name} {row.year}</TableCell>
                          <TableCell className="font-mono text-sm">{row.partner_code}</TableCell>
                          <TableCell className="font-medium">{row.partner_name}</TableCell>
                          <TableCell className="text-right font-semibold text-[#1B4D3E]">{formatCurrency(row.total_commission)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.tds)}</TableCell>
                          <TableCell className="text-right font-semibold text-[#B8860B]">{formatCurrency(row.net_payout)}</TableCell>
                          <TableCell className="text-center">
                            {editingPayoutDate === row.partner_code ? (
                              <div className="flex items-center gap-1 justify-center">
                                <Input type="text" placeholder="dd-MMM-YYYY" value={payoutDateValue} onChange={(e) => setPayoutDateValue(e.target.value)} className="w-36 h-8 text-xs" data-testid={`payout-date-input-${row.partner_code}`} />
                                <Button size="sm" className="h-8 bg-[#1B4D3E]" onClick={() => savePayoutDate(row.partner_code)} data-testid={`save-payout-date-${row.partner_code}`}><CheckCircle className="h-3 w-3" /></Button>
                              </div>
                            ) : (
                              <span className="text-sm">{formatPayoutDate(row.payout_date) || <span className="text-gray-400 italic">—</span>}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" onClick={() => { setEditingPayoutDate(row.partner_code); setPayoutDateValue(row.payout_date || ''); }} data-testid={`edit-payout-date-${row.partner_code}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {payoutRows.length === 0 && (
                        <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#64748b]">No payout data. Select month/year and click "Generate Payout".</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          {/* Config Tab */}
          <TabsContent value="config">
            <Card className="border-0 shadow-sm" data-testid="config-list">
              <CardHeader className="flex flex-row items-center justify-between bg-[#F8FAF9] border-b">
                <CardTitle className="text-lg font-heading text-[#1B4D3E] flex items-center gap-2"><Settings className="h-5 w-5" />Partner Config</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-[#1B4D3E] border-[#1B4D3E]" onClick={fetchPartnerConfigs} disabled={loadingConfigs} data-testid="refresh-configs-btn">
                    {loadingConfigs ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}Refresh
                  </Button>
                  <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={() => openConfigModal()} data-testid="add-config-btn"><Plus className="h-4 w-4 mr-1" />Add Config</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#E8F5E9]/50">
                      <TableHead className="font-semibold text-[#1B4D3E]">Partner Code</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Partner Name</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Allow Add Investment/Redemption</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Allow SubPartner</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Allow My Bonds</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Curated Source</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Hide Commn from SB</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Allow My Bond Commn</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Hide Curated (Client)</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partnerConfigs.map((cfg, idx) => (
                      <TableRow key={cfg.partner_code} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                        <TableCell className="font-mono text-sm">{cfg.partner_code}</TableCell>
                        <TableCell className="font-medium">{cfg.partner_name || '-'}</TableCell>
                        <TableCell className="text-center">{cfg.allow_add_investment_redemption ? <Check className="h-4 w-4 text-green-600 inline" /> : <X className="h-4 w-4 text-red-500 inline" />}</TableCell>
                        <TableCell className="text-center">{cfg.allow_subpartner ? <Check className="h-4 w-4 text-green-600 inline" /> : <X className="h-4 w-4 text-red-500 inline" />}</TableCell>
                        <TableCell className="text-center">{cfg.allow_my_bonds ? <Check className="h-4 w-4 text-green-600 inline" /> : <X className="h-4 w-4 text-red-500 inline" />}</TableCell>
                        <TableCell className="text-center"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#E8F5E9] text-[#1B4D3E]">{cfg.curated_bonds_source === 'combined' ? 'Combined' : cfg.curated_bonds_source === 'only_my_bonds' ? 'Only My Bonds' : 'Only All Bonds'}</span></TableCell>
                        <TableCell className="text-center">{cfg.hide_commission_from_subpartners ? <Check className="h-4 w-4 text-green-600 inline" /> : <X className="h-4 w-4 text-red-500 inline" />}</TableCell>
                        <TableCell className="text-center">{cfg.allow_my_bond_commission ? <Check className="h-4 w-4 text-green-600 inline" /> : <X className="h-4 w-4 text-red-500 inline" />}</TableCell>
                        <TableCell className="text-center">{cfg.hide_curated_from_clients ? <Check className="h-4 w-4 text-green-600 inline" /> : <X className="h-4 w-4 text-red-500 inline" />}</TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="sm" onClick={() => openConfigModal(cfg)} data-testid={`edit-config-${cfg.partner_code}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteConfig(cfg.partner_code)} data-testid={`delete-config-${cfg.partner_code}`}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {partnerConfigs.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#64748b]">{loadingConfigs ? 'Loading...' : 'No partner configs yet. Click "Add Config" to get started.'}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="families">
            <Card className="shadow-md">
              <CardHeader className="bg-gradient-to-r from-[#1B4D3E] to-[#2A6F5A] text-white rounded-t-lg flex flex-row items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2"><Home className="h-5 w-5" />Family Management</CardTitle>
                <Button size="sm" className="bg-white text-[#1B4D3E] hover:bg-gray-100" data-testid="add-family-btn" onClick={() => {
                  if (!partners.length || !clients.length) {
                    Promise.all([
                      axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders()),
                      axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders())
                    ]).then(([pRes, cRes]) => { setPartners(pRes.data); setClients(cRes.data); });
                  }
                  setEditingFamily(null);
                  setFamilyForm({ family_name: '', head_client_id: '', partner_code: '', member_client_ids: [] });
                  setShowFamilyModal(true);
                }}><Plus className="h-4 w-4 mr-1" />Add Family</Button>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#E8F5E9]/50">
                      <TableHead>Family ID</TableHead>
                      <TableHead>Family Name</TableHead>
                      <TableHead>Head</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead className="text-center">Members</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {families.map((fam) => (
                      <TableRow key={fam.family_id}>
                        <TableCell className="font-mono text-xs">{fam.family_id}</TableCell>
                        <TableCell className="font-medium">{fam.family_name}</TableCell>
                        <TableCell>{fam.head_client_name}</TableCell>
                        <TableCell>{fam.partner_code}</TableCell>
                        <TableCell className="text-center">{fam.members?.length || 0}</TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="sm" onClick={() => {
                            if (!partners.length || !clients.length) {
                              Promise.all([
                                axios.get(`${API_URL}/api/admin/partners`, getAuthHeaders()),
                                axios.get(`${API_URL}/api/admin/clients`, getAuthHeaders())
                              ]).then(([pRes, cRes]) => { setPartners(pRes.data); setClients(cRes.data); });
                            }
                            setEditingFamily(fam);
                            setFamilyForm({
                              family_name: fam.family_name,
                              head_client_id: fam.head_client_id,
                              partner_code: fam.partner_code,
                              member_client_ids: fam.members?.filter(m => m.client_id !== fam.head_client_id).map(m => m.client_id) || []
                            });
                            setShowFamilyModal(true);
                          }}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => requestDelete({
                            title: 'Delete Family',
                            description: 'Delete this family? Client data will not be affected.',
                            itemLabel: fam.family_name || fam.family_id,
                            onConfirmed: async () => {
                              await axios.delete(`${API_URL}/api/admin/families/${fam.family_id}`, getAuthHeaders());
                              toast.success('Family deleted');
                              fetchData('families');
                            },
                          })}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {families.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No families created yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bond Holdings Report */}
          <TabsContent value="bond-holdings">
            <Card className="holdings-card border-0" data-testid="bond-holdings-report">
              <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b bg-[#E8F5E9]">
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-[#1B4D3E]"><Briefcase className="h-5 w-5" />Bond-wise Holdings Report</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={bondHoldingsFilter} onValueChange={(v) => { if (v === '__all__') { setBondHoldingsFilter('__all__'); setBondClientBreakdown([]); } else { setBondHoldingsFilter(v); fetchBondHoldingsReport(v); } }}>
                    <SelectTrigger className="w-[320px] h-9 text-sm" data-testid="bond-holdings-filter"><SelectValue placeholder="All Bonds (Summary)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Bonds (Summary)</SelectItem>
                      {bondHoldingsData.map(b => <SelectItem key={b.bond_isin} value={b.bond_isin}>{b.bond_isin} - {b.bond_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="border-[#1B4D3E] text-[#1B4D3E]" onClick={() => { setBondHoldingsFilter('__all__'); setBondClientBreakdown([]); fetchBondHoldingsReport(); }} data-testid="bond-holdings-refresh">Refresh</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loadingBondHoldings ? (
                  <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 rounded-full border-[#1B4D3E]/30 border-t-[#1B4D3E]" /></div>
                ) : bondHoldingsFilter !== '__all__' && bondClientBreakdown.length > 0 ? (
                  <>
                    <div className="px-4 pt-4 pb-2"><p className="text-sm font-medium text-[#1B4D3E]">Client-wise breakdown for: <span className="font-bold">{bondClientBreakdown[0]?.bond_isin} - {bondClientBreakdown[0]?.bond_name}</span></p></div>
                    <Table>
                      <TableHeader><TableRow className="bg-[#E8F5E9]/50">
                        <TableHead className="font-semibold text-[#1B4D3E]">Client Name</TableHead>
                        <TableHead className="font-semibold text-right text-[#1B4D3E]">Net Quantity</TableHead>
                        <TableHead className="font-semibold text-right text-[#1B4D3E]">Invested Value</TableHead>
                        <TableHead className="font-semibold text-right text-[#1B4D3E]">Avg Yield</TableHead>
                        <TableHead className="font-semibold text-center text-[#1B4D3E]">Transactions</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {bondClientBreakdown.map(row => (
                          <TableRow key={row.client_id}>
                            <TableCell className="font-medium">{row.client_name}</TableCell>
                            <TableCell className="text-right font-semibold text-[#1B4D3E]">{row.net_quantity}</TableCell>
                            <TableCell className="text-right font-semibold text-[#D4AF37]">{formatCurrency(row.invested_value)}</TableCell>
                            <TableCell className="text-right">{(row.avg_yield * 100).toFixed(2)}%</TableCell>
                            <TableCell className="text-center"><button className="lens-btn" onClick={() => viewBhTransactions(row)} data-testid={`bh-txn-${row.client_id}`}><Search className="h-4 w-4" /></button></TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-[#E8F5E9] font-bold">
                          <TableCell>Total ({bondClientBreakdown.length} clients)</TableCell>
                          <TableCell className="text-right">{bondClientBreakdown.reduce((s, r) => s + r.net_quantity, 0)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(bondClientBreakdown.reduce((s, r) => s + r.invested_value, 0))}</TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </>
                ) : bondHoldingsFilter !== '__all__' && bondClientBreakdown.length === 0 && !loadingBondHoldings ? (
                  <p className="text-center py-12 text-sm text-[#64748b]">No active holdings found for this bond.</p>
                ) : bondHoldingsData.length > 0 ? (
                  <Table>
                    <TableHeader><TableRow className="bg-[#E8F5E9]/50">
                      <TableHead className="font-semibold text-[#1B4D3E]">Bond ISIN</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Bond Name</TableHead>
                      <TableHead className="font-semibold text-right text-[#1B4D3E]">Net Quantity</TableHead>
                      <TableHead className="font-semibold text-right text-[#1B4D3E]">Invested Value</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Maturity</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {bondHoldingsData.map(b => (
                        <TableRow key={b.bond_isin} className="cursor-pointer hover:bg-[#E8F5E9]/30" onClick={() => { setBondHoldingsFilter(b.bond_isin); fetchBondHoldingsReport(b.bond_isin); }} data-testid={`bond-holding-row-${b.bond_isin}`}>
                          <TableCell className="font-mono text-sm">{b.bond_isin}</TableCell>
                          <TableCell className="font-medium">{b.bond_name}</TableCell>
                          <TableCell className="text-right font-semibold text-[#1B4D3E]">{b.net_quantity}</TableCell>
                          <TableCell className="text-right font-semibold text-[#D4AF37]">{formatCurrency(b.invested_value)}</TableCell>
                          <TableCell className="text-center"><MaturityBadge maturityDate={b.maturity_date} maturityPct1={b.maturity_pct_1} maturityDate2={b.maturity_date_2} maturityPct2={b.maturity_pct_2} maturityDate3={b.maturity_date_3} maturityPct3={b.maturity_pct_3} /></TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-[#E8F5E9] font-bold">
                        <TableCell colSpan={2}>Total ({bondHoldingsData.length} bonds)</TableCell>
                        <TableCell className="text-right">{bondHoldingsData.reduce((s, r) => s + r.net_quantity, 0)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(bondHoldingsData.reduce((s, r) => s + r.invested_value, 0))}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center py-12 text-sm text-[#64748b]">No active bond holdings found.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Keys Management */}
          <TabsContent value="api-keys">
            {/* Aggregate KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="api-keys-kpis">
              <Card className="border-0 bg-gradient-to-br from-[#E8F5E9] to-white">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-[#64748b] flex items-center gap-1"><KeyRound className="h-3 w-3" />Total Keys</p>
                  <p className="text-2xl font-bold text-[#1B4D3E] mt-1" data-testid="kpi-total-keys">{apiKeysSummary.total_keys ?? 0}</p>
                  <p className="text-xs text-[#64748b] mt-0.5"><span className="text-green-700">{apiKeysSummary.active_keys ?? 0} active</span> · <span className="text-red-500">{apiKeysSummary.inactive_keys ?? 0} inactive</span></p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-br from-[#FFF9C4] to-white">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-[#64748b] flex items-center gap-1"><Activity className="h-3 w-3" />Requests Today</p>
                  <p className="text-2xl font-bold text-[#B8860B] mt-1" data-testid="kpi-requests-today">{(apiKeysSummary.requests_today ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-[#64748b] mt-0.5">across all keys</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-br from-[#E0F2FE] to-white">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-[#64748b] flex items-center gap-1"><BarChart3 className="h-3 w-3" />Last 7 Days</p>
                  <p className="text-2xl font-bold text-[#0369A1] mt-1" data-testid="kpi-requests-7d">{(apiKeysSummary.requests_7d ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-[#64748b] mt-0.5">requests</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-br from-[#F3E8FF] to-white">
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-[#64748b] flex items-center gap-1"><TrendingUp className="h-3 w-3" />All-Time Total</p>
                  <p className="text-2xl font-bold text-[#6D28D9] mt-1" data-testid="kpi-requests-total">{(apiKeysSummary.total_requests ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-[#64748b] mt-0.5">{(apiKeysSummary.requests_30d ?? 0).toLocaleString()} in last 30d</p>
                </CardContent>
              </Card>
            </div>
            <Card className="holdings-card border-0" data-testid="api-keys-section">
              <CardHeader className="flex flex-row items-center justify-between border-b bg-[#E8F5E9]">
                <CardTitle className="text-lg font-heading flex items-center gap-2 text-[#1B4D3E]"><KeyRound className="h-5 w-5" />Partner API Keys</CardTitle>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="relative" onClick={() => setShowAlertsPanel(v => !v)} data-testid="alerts-bell-btn">
                    <Bell className="h-4 w-4 mr-1" />
                    Alerts
                    {(apiKeysSummary.unacknowledged_alerts ?? 0) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center" data-testid="alerts-badge">{apiKeysSummary.unacknowledged_alerts}</span>
                    )}
                  </Button>
                  <Button size="sm" className="bg-[#1B4D3E] text-white" onClick={() => { setNewKeyPartnerCode(''); setNewKeyLabel(''); setGeneratedKey(''); setShowNewKeyModal(true); }} data-testid="generate-api-key-btn"><Plus className="h-4 w-4 mr-1" />Generate API Key</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {showAlertsPanel && (
                  <div className="border-b bg-[#FFFBEB] p-4" data-testid="alerts-panel">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-[#92400E] flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Unacknowledged Usage Alerts</p>
                      {apiAlerts.length > 0 && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={acknowledgeAllAlerts} data-testid="ack-all-alerts">Acknowledge All</Button>}
                    </div>
                    {apiAlerts.length === 0 ? (
                      <p className="text-xs text-[#64748b]">No unacknowledged alerts. Good standing.</p>
                    ) : (
                      <div className="space-y-2">
                        {apiAlerts.map(a => (
                          <div key={a.alert_id} className="bg-white border border-[#FCD34D] rounded-md px-3 py-2 flex items-start justify-between gap-3" data-testid={`alert-${a.alert_id}`}>
                            <div className="text-xs">
                              <p className="font-semibold text-[#92400E]">{a.partner_name || a.partner_code} <span className="text-[#64748b] font-normal">· {a.label}</span></p>
                              <p className="text-[#64748b] mt-0.5">Sustained usage: <span className="font-semibold text-[#92400E]">{a.count_in_window}/{a.rate_limit} req</span> in 60s (≥{a.threshold} threshold)</p>
                              <p className="text-[#94a3b8] mt-0.5">{formatDate(a.triggered_at)} · {new Date(a.triggered_at).toLocaleTimeString('en-IN')}</p>
                            </div>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-green-700 hover:bg-green-50" onClick={() => acknowledgeAlert(a.alert_id)} data-testid={`ack-${a.alert_id}`}><Check className="h-3.5 w-3.5 mr-1" />Acknowledge</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {apiKeys.length === 0 ? (
                  <p className="text-center py-12 text-sm text-[#64748b]">No API keys generated yet.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow className="bg-[#E8F5E9]/50">
                      <TableHead className="font-semibold text-[#1B4D3E]">Partner</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Label</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">API Key</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Status</TableHead>
                      <TableHead className="font-semibold text-right text-[#1B4D3E]">Requests</TableHead>
                      <TableHead className="font-semibold text-[#1B4D3E]">Last Used</TableHead>
                      <TableHead className="font-semibold text-center text-[#1B4D3E]">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {apiKeys.map(k => (
                        <TableRow key={k.key_id}>
                          <TableCell className="font-medium">{k.partner_name || k.partner_code}</TableCell>
                          <TableCell className="text-sm">{k.label}</TableCell>
                          <TableCell className="font-mono text-xs">{k.api_key_masked}</TableCell>
                          <TableCell className="text-center">{k.is_active ? <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Active</span> : <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-600">Inactive</span>}</TableCell>
                          <TableCell className="text-right font-mono">{k.total_requests || 0}</TableCell>
                          <TableCell className="text-xs text-[#64748b]">{k.last_used ? formatDate(k.last_used) : 'Never'}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openAnalytics(k.key_id)} title="View usage analytics" data-testid={`analytics-key-${k.key_id}`}><BarChart3 className="h-4 w-4 text-[#0369A1]" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => toggleApiKey(k.key_id)} title={k.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-key-${k.key_id}`}>{k.is_active ? <X className="h-4 w-4 text-red-500" /> : <Check className="h-4 w-4 text-green-600" />}</Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteApiKey(k.key_id, `${k.partner_name || k.partner_code} · ${k.label || ''} (${k.key_id.slice(0, 8)}…)`)} data-testid={`delete-key-${k.key_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className={modalType === 'investment' || modalType === 'redemption' ? 'max-w-2xl' : ''}>
          <DialogHeader><DialogTitle className="font-heading text-[#1B4D3E]">{editingItem ? 'Edit' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}</DialogTitle></DialogHeader>
          <div className={modalType === 'investment' || modalType === 'redemption' ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
            {modalType === 'user' && (<><div><Label>Role</Label><Select value={formData.role || 'client'} onValueChange={(v) => { setFormData(prev => ({ ...prev, role: v, entity_id: '' })); }}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="client">Client</SelectItem><SelectItem value="partner">Partner</SelectItem><SelectItem value="subpartner">SubPartner</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select></div>{formData.role === 'client' && (<div><Label>Select Client</Label><Select value={formData.entity_id || ''} onValueChange={(v) => updateForm('entity_id', v)}><SelectTrigger data-testid="user-entity-select"><SelectValue placeholder="Select Client" /></SelectTrigger><SelectContent>{clients.map(c => (<SelectItem key={c.client_id} value={c.client_id}>{c.client_name} ({c.client_id})</SelectItem>))}</SelectContent></Select></div>)}{formData.role === 'partner' && (<div><Label>Select Partner</Label><Select value={formData.entity_id || ''} onValueChange={(v) => updateForm('entity_id', v)}><SelectTrigger data-testid="user-entity-select"><SelectValue placeholder="Select Partner" /></SelectTrigger><SelectContent>{partners.map(p => (<SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>))}</SelectContent></Select></div>)}{formData.role === 'subpartner' && (<div><Label>Select SubPartner</Label><Select value={formData.entity_id || ''} onValueChange={(v) => updateForm('entity_id', v)}><SelectTrigger data-testid="user-entity-select"><SelectValue placeholder="Select SubPartner" /></SelectTrigger><SelectContent>{subpartners.map(sp => (<SelectItem key={sp.subpartner_code} value={sp.subpartner_code}>{sp.subpartner_name} ({sp.subpartner_code})</SelectItem>))}</SelectContent></Select></div>)}<div><Label>Username</Label><Input value={formData.username || ''} onChange={(e) => updateForm('username', e.target.value)} disabled={!!editingItem} /></div><div><Label>Password {editingItem && '(leave blank to keep)'}</Label><Input type="password" value={formData.password || ''} onChange={(e) => updateForm('password', e.target.value)} /></div></>)}
            {modalType === 'client' && (<><div><Label>Client ID</Label><Input value={formData.client_id || ''} onChange={(e) => updateForm('client_id', e.target.value)} disabled={!!editingItem} data-testid="client-id-input" /></div><div><Label>Client Name</Label><Input value={formData.client_name || ''} onChange={(e) => updateForm('client_name', e.target.value)} data-testid="client-name-input" /></div><div><Label>Select Partner</Label><Select value={formData.partner_code || ''} onValueChange={(v) => { const p = partners.find(pt => pt.partner_code === v); setFormData(prev => ({ ...prev, partner_code: v, partner_name: p?.partner_name || '' })); }}><SelectTrigger data-testid="client-partner-select"><SelectValue placeholder="Select Partner" /></SelectTrigger><SelectContent>{partners.map(p => (<SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>))}</SelectContent></Select></div><div><Label>Email <span className="text-xs text-gray-400 font-normal">(optional)</span></Label><Input type="email" value={formData.email || ''} onChange={(e) => updateForm('email', e.target.value)} placeholder="example@domain.com" data-testid="client-email-input" />{formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>}</div><div><Label>Mobile <span className="text-xs text-gray-400 font-normal">(optional)</span></Label><Input type="tel" value={formData.mobile || ''} onChange={(e) => updateForm('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile number" maxLength={10} data-testid="client-mobile-input" />{formData.mobile && formData.mobile.length > 0 && formData.mobile.length !== 10 && <p className="text-xs text-red-500 mt-1">Mobile number must be 10 digits</p>}</div><div><Label>CMR (PDF) {!editingItem && <span className="text-xs text-red-500 font-normal">*required</span>}</Label>{editingItem?.cmr_path && !cmrFile && (<p className="text-xs text-green-600 mb-1">CMR already uploaded: {editingItem.cmr_filename || 'cmr.pdf'}</p>)}<Input type="file" accept=".pdf" onChange={(e) => setCmrFile(e.target.files?.[0] || null)} data-testid="client-cmr-input" />{!editingItem && !cmrFile && <p className="text-xs text-gray-500 mt-1">Upload Client Master Record (PDF only)</p>}</div></>)}
            {modalType === 'partner' && (<><div><Label>Partner Code</Label><Input value={formData.partner_code || ''} onChange={(e) => updateForm('partner_code', e.target.value.trim())} data-testid="partner-code-input" />{editingItem && formData.partner_code && formData.partner_code !== editingItem.partner_code && (<p className="text-xs text-amber-700 mt-1 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /><span>Renaming partner code from <code className="px-1 py-0.5 bg-amber-50 rounded text-[10px] font-mono">{editingItem.partner_code}</code> → <code className="px-1 py-0.5 bg-amber-50 rounded text-[10px] font-mono">{formData.partner_code}</code> will cascade across all clients, subpartners, investments, redemptions, commissions, themes, API keys & user logins.</span></p>)}</div><div><Label>Partner Name</Label><Input value={formData.partner_name || ''} onChange={(e) => updateForm('partner_name', e.target.value)} /></div><div><Label>Email <span className="text-xs text-gray-400 font-normal">(optional)</span></Label><Input type="email" value={formData.email || ''} onChange={(e) => updateForm('email', e.target.value)} placeholder="example@domain.com" data-testid="partner-email-input" />{formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>}</div><div><Label>Mobile <span className="text-xs text-gray-400 font-normal">(optional)</span></Label><Input type="tel" value={formData.mobile || ''} onChange={(e) => updateForm('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile number" maxLength={10} data-testid="partner-mobile-input" />{formData.mobile && formData.mobile.length > 0 && formData.mobile.length !== 10 && <p className="text-xs text-red-500 mt-1">Mobile number must be 10 digits</p>}</div><div className="col-span-2"><Label>Partner Logo URL (optional)</Label><Input value={formData.partner_logo_url || ''} onChange={(e) => updateForm('partner_logo_url', e.target.value)} placeholder="https://example.com/logo.png" /><p className="text-xs text-[#64748b] mt-1">Logo shown in partner & client dashboards instead of BondMaster logo</p></div><div className="col-span-2"><Label>Partner Favicon URL (optional)</Label><Input value={formData.partner_favicon_url || ''} onChange={(e) => updateForm('partner_favicon_url', e.target.value)} placeholder="https://example.com/favicon.png" data-testid="partner-favicon-input" /><p className="text-xs text-[#64748b] mt-1">Favicon/icon shown in browser tab for partner, subpartner & client logins</p></div><div className="col-span-2"><Label>Partner Website URL (optional)</Label><Input value={formData.partner_web_url || ''} onChange={(e) => updateForm('partner_web_url', e.target.value)} placeholder="https://partner-website.com" /><p className="text-xs text-[#64748b] mt-1">Redirect URL on sign out for partner & their clients</p></div><div className="col-span-2 border-t pt-3 mt-1"><div className="flex items-center gap-2 mb-2"><input type="checkbox" id="has-custom-login" checked={!!formData.has_custom_login_page} onChange={(e) => updateForm('has_custom_login_page', e.target.checked)} className="h-4 w-4 accent-[#1B4D3E]" data-testid="partner-has-custom-login" /><Label htmlFor="has-custom-login" className="font-medium cursor-pointer">Has Custom Login Page</Label></div><p className="text-xs text-[#64748b]">When enabled, the partner gets a branded sign-in URL at <code className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-mono">/{(formData.partner_code || 'partner_code').toLowerCase()}/login</code> using their logo, favicon and color theme. Only users belonging to this partner can sign in there.</p>{!!formData.has_custom_login_page && formData.partner_code && (() => { const url = `${window.location.origin}/${formData.partner_code.toLowerCase()}/login`; return (<div className="mt-2 flex items-center gap-2 bg-[#E8F5E9] border border-[#1B4D3E]/20 rounded-md px-2 py-1.5" data-testid="custom-login-url-preview"><span className="text-[10px] font-semibold text-[#1B4D3E] uppercase tracking-wide shrink-0">URL</span><code className="flex-1 text-xs font-mono text-[#1B4D3E] break-all" data-testid="custom-login-url-value">{url}</code><Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs shrink-0" onClick={() => { navigator.clipboard?.writeText(url).then(() => toast.success('Login URL copied')).catch(() => toast.error('Copy failed')); }} data-testid="copy-custom-login-url"><Check className="h-3 w-3 mr-1" />Copy</Button><Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs shrink-0" onClick={() => window.open(url, '_blank', 'noopener')} data-testid="open-custom-login-url"><Eye className="h-3 w-3 mr-1" />Open</Button><Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs shrink-0" onClick={() => { setQrUrl(url); setQrPartnerName(formData.partner_name || formData.partner_code); setQrLogoUrl(formData.partner_logo_url || formData.partner_favicon_url || ''); setQrShowLogo(true); setShowQrModal(true); }} data-testid="qr-custom-login-url"><QrCode className="h-3 w-3 mr-1" />QR</Button></div>); })()}</div><div className="col-span-2"><Label>Partner App Name (optional)</Label><Input value={formData.partner_app_name || ''} onChange={(e) => updateForm('partner_app_name', e.target.value)} placeholder="BondMaster" data-testid="partner-app-name-input" /><p className="text-xs text-[#64748b] mt-1">App name shown when users "Add to Home Screen" on iOS/Android/Windows. Defaults to "BondMaster" if blank.</p></div></>)}
            {modalType === 'investment' && (<><div><Label>Transaction ID</Label><Input value={formData.transaction_id || ''} onChange={(e) => updateForm('transaction_id', e.target.value)} disabled={!!editingItem} data-testid="inv-txn-id" /></div><div><Label>Transaction Date</Label><Input value={formData.transaction_date || ''} onChange={(e) => updateForm('transaction_date', e.target.value)} placeholder="DD-MM-YYYY" data-testid="inv-txn-date" /></div><div><Label>Bond ISIN</Label><Input value={formData.bond_isin || ''} onChange={(e) => updateForm('bond_isin', e.target.value)} /></div><div><Label>Bond Name</Label><Input value={formData.bond_name || ''} onChange={(e) => updateForm('bond_name', e.target.value)} /></div><div><Label>Quantity</Label><Input type="number" value={formData.quantity || ''} onChange={(e) => updateForm('quantity', e.target.value)} /></div><div><Label>Face Value</Label><Input type="number" value={formData.face_value || ''} onChange={(e) => updateForm('face_value', e.target.value)} /></div><div><Label>Invested Value</Label><Input type="number" value={formData.invested_value || ''} onChange={(e) => updateForm('invested_value', e.target.value)} /></div><div><Label>Yield (%)</Label><Input type="number" step="0.01" value={formData.yield_rate || ''} onChange={(e) => updateForm('yield_rate', e.target.value)} placeholder="e.g. 8.5" data-testid="inv-yield" /></div><div><Label>Maturity Date</Label><Input value={formData.maturity_date || ''} onChange={(e) => updateForm('maturity_date', e.target.value)} placeholder="DD-MM-YYYY" data-testid="inv-maturity-date" /></div><div><Label>1st Maturity %</Label><Input type="number" min="0" max="100" value={formData.maturity_pct_1 ?? '100'} onChange={(e) => updateForm('maturity_pct_1', e.target.value)} placeholder="100" data-testid="inv-maturity-pct-1" /></div>{Number(formData.maturity_pct_1 || 100) < 100 && (<><div><Label>2nd Maturity Date</Label><Input value={formData.maturity_date_2 || ''} onChange={(e) => updateForm('maturity_date_2', e.target.value)} placeholder="DD-MM-YYYY" data-testid="inv-maturity-date-2" /></div><div><Label>2nd Maturity %</Label><Input type="number" min="0" max="100" value={formData.maturity_pct_2 || ''} onChange={(e) => updateForm('maturity_pct_2', e.target.value)} data-testid="inv-maturity-pct-2" /></div><div><Label>3rd Maturity Date</Label><Input value={formData.maturity_date_3 || ''} onChange={(e) => updateForm('maturity_date_3', e.target.value)} placeholder="DD-MM-YYYY" data-testid="inv-maturity-date-3" /></div><div><Label>3rd Maturity %</Label><Input type="number" min="0" max="100" value={formData.maturity_pct_3 || ''} onChange={(e) => updateForm('maturity_pct_3', e.target.value)} data-testid="inv-maturity-pct-3" /></div></>)}{Number(formData.maturity_pct_1 || 100) < 100 && (() => { const t = (Number(formData.maturity_pct_1) || 0) + (Number(formData.maturity_pct_2) || 0) + (Number(formData.maturity_pct_3) || 0); return <div className="col-span-2"><p className={`text-xs font-medium px-2 py-1 rounded ${t === 100 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="maturity-pct-total">Maturity % Total: {t}% {t === 100 ? '(Valid)' : '(Must equal 100%)'}</p></div>; })()}<div><Label>Partner Share (%)</Label><Input type="number" step="0.01" value={formData.partner_share || ''} onChange={(e) => updateForm('partner_share', e.target.value)} placeholder="e.g. 0.5" data-testid="inv-partner-share" /></div><div><Label>Select Partner</Label><Select value={formData.partner_code || ''} onValueChange={(v) => { const p = partners.find(pt => pt.partner_code === v); setFormData(prev => ({ ...prev, partner_code: v, partner_name: p?.partner_name || '', client_id: '', client_name: '' })); }}><SelectTrigger data-testid="inv-partner-select"><SelectValue placeholder="Select Partner" /></SelectTrigger><SelectContent>{partners.map(p => (<SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>))}</SelectContent></Select></div><div><Label>Select Client</Label><Select value={formData.client_id || ''} onValueChange={(v) => { const c = clients.find(cl => cl.client_id === v); setFormData(prev => ({ ...prev, client_id: v, client_name: c?.client_name || '' })); }}><SelectTrigger data-testid="inv-client-select"><SelectValue placeholder={formData.partner_code ? 'Select Client' : 'Select Partner first'} /></SelectTrigger><SelectContent>{clients.filter(c => c.partner_code === formData.partner_code).map(c => (<SelectItem key={c.client_id} value={c.client_id}>{c.client_name} ({c.client_id})</SelectItem>))}</SelectContent></Select></div><div><Label>Commission Type</Label><Select value={formData.commn_type || 'Trail'} onValueChange={(v) => updateForm('commn_type', v)}><SelectTrigger data-testid="commn-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Trail">Trail</SelectItem><SelectItem value="OneTime">OneTime</SelectItem></SelectContent></Select></div></>)}
            {modalType === 'redemption' && (<>{!editingItem ? (<><div><Label>Client <span className="text-red-500">*</span></Label><Select value={redClientId} onValueChange={(v) => { setRedClientId(v); setFormData(prev => ({ ...prev, investment_txn_id: '' })); fetchRedInvestmentOptions(v); }}><SelectTrigger data-testid="admin-red-client-select"><SelectValue placeholder="Select client first" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.client_name}</SelectItem>)}</SelectContent></Select></div><div><Label>Investment Transaction <span className="text-red-500">*</span></Label><Select value={formData.investment_txn_id || ''} onValueChange={(v) => updateForm('investment_txn_id', v)} disabled={!redClientId}><SelectTrigger data-testid="admin-red-inv-select"><SelectValue placeholder={redClientId ? 'Select investment' : 'Select a client first'} /></SelectTrigger><SelectContent>{redInvestmentOptions.map(inv => <SelectItem key={inv.transaction_id} value={inv.transaction_id}>{inv.transaction_date?.split(' ')[0]} - {inv.bond_isin} - {inv.bond_name} (Qty: {inv.quantity})</SelectItem>)}</SelectContent></Select></div><div><Label>Transaction Date <span className="text-red-500">*</span></Label><Input type="date" value={formData.transaction_date || ''} onChange={(e) => updateForm('transaction_date', e.target.value)} data-testid="admin-red-date" /></div><div><Label>Quantity <span className="text-red-500">*</span></Label><Input type="number" value={formData.quantity || ''} onChange={(e) => updateForm('quantity', e.target.value)} placeholder="Qty" data-testid="admin-red-qty" /></div><div><Label>Redemption Value <span className="text-red-500">*</span></Label><Input type="number" value={formData.redemption_value || ''} onChange={(e) => updateForm('redemption_value', e.target.value)} placeholder="Redemption value" data-testid="admin-red-value" /></div></>) : (<><div><Label>Transaction ID</Label><Input value={formData.transaction_id || ''} disabled /></div><div><Label>Bond</Label><Input value={`${formData.bond_isin || ''} - ${formData.bond_name || ''}`} disabled /></div><div><Label>Client</Label><Input value={formData.client_name || ''} disabled /></div><div><Label>Transaction Date</Label><Input value={formData.transaction_date || ''} onChange={(e) => updateForm('transaction_date', e.target.value)} placeholder="YYYY-MM-DD" /></div><div><Label>Quantity</Label><Input type="number" value={formData.quantity || ''} onChange={(e) => updateForm('quantity', e.target.value)} /></div><div><Label>Redemption Value</Label><Input type="number" value={formData.redemption_value || ''} onChange={(e) => updateForm('redemption_value', e.target.value)} /></div></>)}</>)}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={handleSave}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-[#1B4D3E]">Import Excel Data</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#64748b]">Upload an Excel file with "Investment" and "Redemption" sheets.</p>
            <div className="border-2 border-dashed border-[#E8F5E9] rounded-lg p-8 text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-[#1B4D3E] mb-4" />
              <input type="file" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files[0])} className="hidden" id="excel-upload" />
              <label htmlFor="excel-upload" className="cursor-pointer"><span className="text-[#1B4D3E] font-medium hover:underline">Choose file</span><span className="text-[#64748b]"> or drag and drop</span></label>
              {importFile && <p className="mt-2 text-sm text-[#1B4D3E] font-medium">{importFile.name}</p>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button><Button className="bg-[#D4AF37] hover:bg-[#B8860B]" onClick={handleImport} disabled={importing}>{importing ? 'Importing...' : 'Import Data'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showTransactionsModal} onOpenChange={setShowTransactionsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-[#1B4D3E]">Transaction Details</DialogTitle>{selectedHolding && <p className="text-sm text-[#64748b]">{selectedHolding.bond_name} ({selectedHolding.bond_isin})</p>}</DialogHeader>
          <div className="space-y-6 mt-4">
            <div><h3 className="font-semibold text-[#1B4D3E] mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4" />Investment Transactions</h3>
              <Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead className="text-[#1B4D3E]">Date</TableHead><TableHead className="text-[#1B4D3E]">ID</TableHead><TableHead className="text-[#1B4D3E] text-right">Qty</TableHead><TableHead className="text-[#1B4D3E] text-right">Invested</TableHead><TableHead className="text-[#1B4D3E] text-right">Yield</TableHead></TableRow></TableHeader>
              <TableBody>{transactions.investments.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.invested_value)}</TableCell><TableCell className="text-right text-[#B8860B]">{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{transactions.investments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-[#64748b]">No investment transactions</TableCell></TableRow>}</TableBody></Table></div>
            <div><h3 className="font-semibold text-[#B8860B] mb-3 flex items-center gap-2"><Download className="h-4 w-4" />Redemption Transactions</h3>
              <Table><TableHeader><TableRow className="bg-[#FFF9C4]/30"><TableHead className="text-[#B8860B]">Date</TableHead><TableHead className="text-[#B8860B]">ID</TableHead><TableHead className="text-[#B8860B] text-right">Qty</TableHead><TableHead className="text-[#B8860B] text-right">Redemption Value</TableHead><TableHead className="text-[#B8860B] text-right">Yield</TableHead></TableRow></TableHeader>
              <TableBody>{transactions.redemptions.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.redemption_value)}</TableCell><TableCell className="text-right text-[#B8860B]">{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{transactions.redemptions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-[#64748b]">No redemption transactions</TableCell></TableRow>}</TableBody></Table></div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Color Theme Editor Modal */}
      <Dialog open={showColorModal} onOpenChange={setShowColorModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E] flex items-center gap-2"><Palette className="h-5 w-5" />{editingColorConfig ? 'Edit' : 'Create'} Partner Theme</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Color Pickers */}
            <div className="space-y-4">
              {!editingColorConfig && (
                <div>
                  <Label>Partner</Label>
                  <Select value={colorFormData.partner_code} onValueChange={(v) => updateColor('partner_code', v)}>
                    <SelectTrigger data-testid="theme-partner-select"><SelectValue placeholder="Select a partner" /></SelectTrigger>
                    <SelectContent>
                      {partnersWithoutTheme.map(p => (
                        <SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editingColorConfig && (
                <div className="p-3 bg-[#F8FAF9] rounded-lg">
                  <p className="text-xs text-[#64748b]">Editing theme for</p>
                  <p className="font-semibold text-[#1B4D3E]">{partners.find(p => p.partner_code === colorFormData.partner_code)?.partner_name || colorFormData.partner_code}</p>
                </div>
              )}
              {/* Preset Templates */}
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Quick Start from Preset</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="theme-presets">
                  {THEME_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => setColorFormData(prev => ({ ...prev, ...preset.colors }))}
                      className="group relative flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-gray-200 hover:border-gray-400 hover:shadow-sm transition-all text-center"
                      data-testid={`preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex gap-0.5">
                        {[preset.colors.primary, preset.colors.accent, preset.colors.light_bg].map((c, i) => (
                          <div key={c} className="w-4 h-4 rounded-full border border-gray-100" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      <span className="text-[10px] font-medium text-gray-600 leading-tight">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              {[
                { key: 'primary', label: 'Primary Color', desc: 'Buttons, headers, main brand' },
                { key: 'primary_hover', label: 'Primary Hover', desc: 'Button hover state' },
                { key: 'accent', label: 'Accent Color', desc: 'Highlights, values, emphasis' },
                { key: 'light_bg', label: 'Light Background', desc: 'Card highlights, badges' },
                { key: 'card_bg', label: 'Card Background', desc: 'Table headers, card bg' },
                { key: 'sidebar_bg', label: 'Sidebar Background', desc: 'Sidebar panel' },
                { key: 'text_primary', label: 'Primary Text', desc: 'Headings, labels' },
                { key: 'text_secondary', label: 'Secondary Text', desc: 'Descriptions, hints' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="color"
                      value={colorFormData[key] || '#000000'}
                      onChange={(e) => updateColor(key, e.target.value)}
                      className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200 p-0.5"
                      data-testid={`color-picker-${key}`}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{label}</p>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                  <Input
                    value={colorFormData[key] || ''}
                    onChange={(e) => updateColor(key, e.target.value)}
                    className="w-24 font-mono text-xs h-8"
                    data-testid={`color-input-${key}`}
                  />
                </div>
              ))}
            </div>
            {/* Live Preview */}
            <div className="space-y-4">
              <p className="text-sm font-semibold text-[#1B4D3E]">Live Preview</p>
              <div className="border rounded-xl overflow-hidden shadow-sm" data-testid="theme-preview">
                {/* Sidebar preview */}
                <div className="p-4 border-b" style={{ backgroundColor: colorFormData.sidebar_bg }}>
                  <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: colorFormData.light_bg }}>
                    <Users className="h-4 w-4" style={{ color: colorFormData.accent }} />
                    <div>
                      <p className="text-[10px]" style={{ color: colorFormData.text_secondary }}>Partner</p>
                      <p className="text-xs font-medium" style={{ color: colorFormData.accent }}>Sample Partner</p>
                    </div>
                  </div>
                </div>
                {/* Header preview */}
                <div className="p-4 border-b bg-white">
                  <p className="text-base font-bold" style={{ color: colorFormData.text_primary }}>Partner Dashboard</p>
                  <p className="text-xs" style={{ color: colorFormData.text_secondary }}>View and manage your clients</p>
                </div>
                {/* Summary cards preview */}
                <div className="p-4 grid grid-cols-2 gap-3 bg-white">
                  <div className="rounded-lg p-3 shadow-sm border" style={{ backgroundColor: colorFormData.card_bg }}>
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded" style={{ backgroundColor: colorFormData.light_bg }}>
                        <Users className="h-3.5 w-3.5" style={{ color: colorFormData.primary }} />
                      </div>
                      <div>
                        <p className="text-[10px]" style={{ color: colorFormData.text_secondary }}>Clients</p>
                        <p className="text-sm font-bold" style={{ color: colorFormData.primary }}>12</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg p-3 shadow-sm border" style={{ backgroundColor: colorFormData.card_bg }}>
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded" style={{ backgroundColor: `${colorFormData.accent}20` }}>
                        <Wallet className="h-3.5 w-3.5" style={{ color: colorFormData.accent }} />
                      </div>
                      <div>
                        <p className="text-[10px]" style={{ color: colorFormData.text_secondary }}>Invested</p>
                        <p className="text-sm font-bold" style={{ color: colorFormData.accent }}>{formatCurrency(4250000)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Table preview */}
                <div className="px-4 pb-4 bg-white">
                  <div className="rounded-lg border overflow-hidden">
                    <div className="px-3 py-2 text-xs font-semibold" style={{ backgroundColor: `${colorFormData.light_bg}`, color: colorFormData.primary }}>Client Name</div>
                    <div className="px-3 py-2 text-xs border-t flex justify-between">
                      <span>Sample Client</span>
                      <span className="font-semibold" style={{ color: colorFormData.accent }}>{formatCurrency(1200000)}</span>
                    </div>
                    <div className="px-3 py-2 text-xs border-t flex justify-between">
                      <span>Another Client</span>
                      <span className="font-semibold" style={{ color: colorFormData.accent }}>{formatCurrency(850000)}</span>
                    </div>
                  </div>
                </div>
                {/* Button preview */}
                <div className="p-4 bg-white border-t flex gap-2">
                  <button className="px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: colorFormData.primary }}>Primary Button</button>
                  <button className="px-3 py-1.5 rounded-md text-xs font-medium border" style={{ borderColor: colorFormData.primary, color: colorFormData.primary }}>Outline</button>
                  <button className="px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: colorFormData.accent }}>Accent</button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowColorModal(false)}>Cancel</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={saveColorConfig} data-testid="save-theme-btn">
              {editingColorConfig ? 'Update Theme' : 'Create Theme'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bond Add/Edit Dialog */}
      <Dialog open={showBondModal} onOpenChange={setShowBondModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E]">{editingBond ? 'Edit' : 'Add'} Bond</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Bond ISIN <span className="text-red-500">*</span></Label>
              <Input value={bondForm.bond_isin} onChange={(e) => setBondForm(prev => ({ ...prev, bond_isin: e.target.value }))} disabled={!!editingBond} placeholder="e.g. INE001A07XXX" data-testid="bond-isin-input" />
            </div>
            <div>
              <Label>Bond Name <span className="text-red-500">*</span></Label>
              <Input value={bondForm.bond_name} onChange={(e) => setBondForm(prev => ({ ...prev, bond_name: e.target.value }))} placeholder="Bond name" data-testid="bond-name-input" />
            </div>
            <div>
              <Label>Rating</Label>
              <Input value={bondForm.rating} onChange={(e) => setBondForm(prev => ({ ...prev, rating: e.target.value }))} placeholder="e.g. AAA, AA+, A" data-testid="bond-rating-input" />
            </div>
            <div>
              <Label>Yield (%)</Label>
              <Input type="number" step="0.01" value={bondForm.yield_rate} onChange={(e) => setBondForm(prev => ({ ...prev, yield_rate: e.target.value }))} placeholder="e.g. 8.5" data-testid="bond-yield-input" />
            </div>
            <div>
              <Label>Maturity Date</Label>
              <Input type="date" value={bondForm.maturity_date} onChange={(e) => setBondForm(prev => ({ ...prev, maturity_date: e.target.value }))} data-testid="bond-maturity-input" />
            </div>
            <div>
              <Label>1st Maturity %</Label>
              <Input type="number" min="0" max="100" value={bondForm.maturity_pct_1 ?? '100'} onChange={(e) => setBondForm(prev => ({ ...prev, maturity_pct_1: e.target.value }))} placeholder="100" data-testid="bond-maturity-pct-1" />
            </div>
            {Number(bondForm.maturity_pct_1 || 100) < 100 && (<>
            <div>
              <Label>2nd Maturity Date</Label>
              <Input type="date" value={bondForm.maturity_date_2 || ''} onChange={(e) => setBondForm(prev => ({ ...prev, maturity_date_2: e.target.value }))} data-testid="bond-maturity-date-2" />
            </div>
            <div>
              <Label>2nd Maturity %</Label>
              <Input type="number" min="0" max="100" value={bondForm.maturity_pct_2 || ''} onChange={(e) => setBondForm(prev => ({ ...prev, maturity_pct_2: e.target.value }))} data-testid="bond-maturity-pct-2" />
            </div>
            <div>
              <Label>3rd Maturity Date</Label>
              <Input type="date" value={bondForm.maturity_date_3 || ''} onChange={(e) => setBondForm(prev => ({ ...prev, maturity_date_3: e.target.value }))} data-testid="bond-maturity-date-3" />
            </div>
            <div>
              <Label>3rd Maturity %</Label>
              <Input type="number" min="0" max="100" value={bondForm.maturity_pct_3 || ''} onChange={(e) => setBondForm(prev => ({ ...prev, maturity_pct_3: e.target.value }))} data-testid="bond-maturity-pct-3" />
            </div>
            {(() => { const t = (Number(bondForm.maturity_pct_1) || 0) + (Number(bondForm.maturity_pct_2) || 0) + (Number(bondForm.maturity_pct_3) || 0); return <div className="col-span-2"><p className={`text-xs font-medium px-2 py-1 rounded ${t === 100 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="bond-maturity-pct-total">Maturity % Total: {t}% {t === 100 ? '(Valid)' : '(Must equal 100%)'}</p></div>; })()}
            </>)}
            <div>
              <Label>Interest Payout Frequency</Label>
              <Select value={bondForm.interest_payout_frequency} onValueChange={(v) => setBondForm(prev => ({ ...prev, interest_payout_frequency: v }))}>
                <SelectTrigger data-testid="bond-frequency-input"><SelectValue placeholder="Select frequency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Semi-Annual">Semi-Annual</SelectItem>
                  <SelectItem value="Annual">Annual</SelectItem>
                  <SelectItem value="At Maturity">At Maturity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Available</Label>
              <Select value={bondForm.available ? 'yes' : 'no'} onValueChange={(v) => setBondForm(prev => ({ ...prev, available: v === 'yes' }))}>
                <SelectTrigger data-testid="bond-available-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Bond Image</Label>
              <div className="space-y-2">
                <Input value={bondForm.bond_image_url} onChange={(e) => { setBondForm(prev => ({ ...prev, bond_image_url: e.target.value })); setBondImageFile(null); }} placeholder="Paste image URL here" data-testid="bond-image-url-input" />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#64748b]">or upload:</span>
                  <input type="file" accept="image/*" onChange={(e) => { if (e.target.files[0]) { setBondImageFile(e.target.files[0]); setBondForm(prev => ({ ...prev, bond_image_url: '' })); } }} className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-[#E8F5E9] file:text-[#1B4D3E] file:cursor-pointer" data-testid="bond-image-file-input" />
                </div>
                {(bondForm.bond_image_url || bondImageFile) && (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <Check className="h-3 w-3" />
                    {bondImageFile ? `File: ${bondImageFile.name}` : 'URL provided'}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBondModal(false)}>Cancel</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={handleSaveBond} disabled={uploadingBondImage || !bondForm.bond_isin.trim() || !bondForm.bond_name.trim()} data-testid="save-bond-btn">
              {uploadingBondImage ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Plus className="h-4 w-4 mr-2" />}
              {editingBond ? 'Update Bond' : 'Add Bond'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Partner Config Dialog */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E]">{editingConfig ? 'Edit' : 'Add'} Partner Config</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Partner <span className="text-red-500">*</span></Label>
              <Select value={configForm.partner_code} onValueChange={(v) => setConfigForm(prev => ({ ...prev, partner_code: v }))} disabled={!!editingConfig}>
                <SelectTrigger data-testid="config-partner-select"><SelectValue placeholder="Select a partner" /></SelectTrigger>
                <SelectContent>
                  {(editingConfig ? partners : partners.filter(p => !partnerConfigs.some(c => c.partner_code === p.partner_code))).map(p => (
                    <SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm font-medium">Allow Add Investment/Redemption</Label>
                <p className="text-xs text-[#64748b] mt-0.5">Partner can add and edit investments &amp; redemptions</p>
              </div>
              <Button variant={configForm.allow_add_investment_redemption ? 'default' : 'outline'} size="sm" className={configForm.allow_add_investment_redemption ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setConfigForm(prev => ({ ...prev, allow_add_investment_redemption: !prev.allow_add_investment_redemption }))} data-testid="config-toggle-investment">
                {configForm.allow_add_investment_redemption ? <><Check className="h-4 w-4 mr-1" />Yes</> : <><X className="h-4 w-4 mr-1" />No</>}
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm font-medium">Allow SubPartner</Label>
                <p className="text-xs text-[#64748b] mt-0.5">Partner can create and manage SubPartners</p>
              </div>
              <Button variant={configForm.allow_subpartner ? 'default' : 'outline'} size="sm" className={configForm.allow_subpartner ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setConfigForm(prev => ({ ...prev, allow_subpartner: !prev.allow_subpartner }))} data-testid="config-toggle-subpartner">
                {configForm.allow_subpartner ? <><Check className="h-4 w-4 mr-1" />Yes</> : <><X className="h-4 w-4 mr-1" />No</>}
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm font-medium">Allow My Bonds</Label>
                <p className="text-xs text-[#64748b] mt-0.5">Partner can create and manage their own bonds list</p>
              </div>
              <Button variant={configForm.allow_my_bonds ? 'default' : 'outline'} size="sm" className={configForm.allow_my_bonds ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setConfigForm(prev => ({ ...prev, allow_my_bonds: !prev.allow_my_bonds }))} data-testid="config-toggle-mybonds">
                {configForm.allow_my_bonds ? <><Check className="h-4 w-4 mr-1" />Yes</> : <><X className="h-4 w-4 mr-1" />No</>}
              </Button>
            </div>
            <div className="p-3 border rounded-lg">
              <Label className="text-sm font-medium">Curated Bonds Data Source</Label>
              <p className="text-xs text-[#64748b] mt-0.5 mb-2">Determines which bonds appear in the Curated Bonds report</p>
              <Select value={configForm.curated_bonds_source} onValueChange={(v) => setConfigForm(prev => ({ ...prev, curated_bonds_source: v }))}>
                <SelectTrigger data-testid="config-curated-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="only_all_bonds">Only All Bonds (Admin)</SelectItem>
                  <SelectItem value="only_my_bonds">Only My Bonds (Partner)</SelectItem>
                  <SelectItem value="combined">Combined (All + My Bonds)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm font-medium">Don't Show Commission to Sub-Brokers</Label>
                <p className="text-xs text-[#64748b] mt-0.5">Hide Commission and Payout tabs from SubPartners of this partner</p>
              </div>
              <Button variant={configForm.hide_commission_from_subpartners ? 'default' : 'outline'} size="sm" className={configForm.hide_commission_from_subpartners ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setConfigForm(prev => ({ ...prev, hide_commission_from_subpartners: !prev.hide_commission_from_subpartners }))} data-testid="config-toggle-hide-commission-sb">
                {configForm.hide_commission_from_subpartners ? <><Check className="h-4 w-4 mr-1" />Yes</> : <><X className="h-4 w-4 mr-1" />No</>}
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-sm font-medium">Allow My Bond Commission</Label>
                <p className="text-xs text-[#64748b] mt-0.5">Enable My Bond Commission & Payout modules for this partner</p>
              </div>
              <Button variant={configForm.allow_my_bond_commission ? 'default' : 'outline'} size="sm" className={configForm.allow_my_bond_commission ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setConfigForm(prev => ({ ...prev, allow_my_bond_commission: !prev.allow_my_bond_commission }))} data-testid="config-toggle-my-bond-commission">
                {configForm.allow_my_bond_commission ? <><Check className="h-4 w-4 mr-1" />Yes</> : <><X className="h-4 w-4 mr-1" />No</>}
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div>
                <Label className="text-sm font-medium">Hide Curated Bonds in Client Login</Label>
                <p className="text-xs text-[#64748b] mt-0.5">Hide Curated Bonds tab from clients mapped to this partner</p>
              </div>
              <Button variant={configForm.hide_curated_from_clients ? 'default' : 'outline'} size="sm" className={configForm.hide_curated_from_clients ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setConfigForm(prev => ({ ...prev, hide_curated_from_clients: !prev.hide_curated_from_clients }))} data-testid="config-toggle-hide-curated-clients">
                {configForm.hide_curated_from_clients ? <><Check className="h-4 w-4 mr-1" />Yes</> : <><X className="h-4 w-4 mr-1" />No</>}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163d32]" onClick={handleSaveConfig} disabled={!configForm.partner_code} data-testid="save-config-btn">
              {editingConfig ? 'Update Config' : 'Save Config'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bond Image Preview Dialog */}
      <Dialog open={showBondImagePreview} onOpenChange={setShowBondImagePreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E]">Bond Details Image</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <img src={previewBondImage} alt="Bond Details" className="max-w-full max-h-[55vh] object-contain rounded-lg border" data-testid="bond-image-preview" />
            <a href={previewBondImage} download="bond_details.png" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="text-[#1B4D3E] border-[#1B4D3E]" data-testid="download-bond-image-btn"><Download className="h-4 w-4 mr-2" />Download Image</Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Family Add/Edit Modal */}
      <Dialog open={showFamilyModal} onOpenChange={setShowFamilyModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-[#1B4D3E]">{editingFamily ? 'Edit' : 'Add'} Family</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Family Name</Label><Input value={familyForm.family_name} onChange={(e) => setFamilyForm(prev => ({ ...prev, family_name: e.target.value }))} placeholder="Enter family name" data-testid="family-name-input" /></div>
            {!editingFamily && (
              <div><Label>Select Partner</Label><Select value={familyForm.partner_code} onValueChange={(v) => setFamilyForm(prev => ({ ...prev, partner_code: v, head_client_id: '', member_client_ids: [] }))}><SelectTrigger data-testid="family-partner-select"><SelectValue placeholder="Select Partner" /></SelectTrigger><SelectContent>{partners.map(p => (<SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>))}</SelectContent></Select></div>
            )}
            {!editingFamily && (
              <div><Label>Family Head (Client)</Label><Select value={familyForm.head_client_id} onValueChange={(v) => setFamilyForm(prev => ({ ...prev, head_client_id: v }))}><SelectTrigger data-testid="family-head-select"><SelectValue placeholder={familyForm.partner_code ? "Select Head Client" : "Select Partner first"} /></SelectTrigger><SelectContent>{clients.filter(c => c.partner_code === familyForm.partner_code).map(c => (<SelectItem key={c.client_id} value={c.client_id}>{c.client_name} ({c.client_id})</SelectItem>))}</SelectContent></Select></div>
            )}
            {editingFamily && (
              <div><Label>Family Head</Label><Input value={`${editingFamily.head_client_name} (${editingFamily.head_client_id})`} disabled className="bg-gray-50" /></div>
            )}
            <div>
              <Label>Family Members</Label>
              <p className="text-xs text-gray-500 mb-2">Head is automatically included. Select additional members below.</p>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {clients.filter(c => c.partner_code === (editingFamily ? editingFamily.partner_code : familyForm.partner_code) && c.client_id !== familyForm.head_client_id).map(c => (
                  <label key={c.client_id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" className="rounded" checked={familyForm.member_client_ids.includes(c.client_id)} onChange={(e) => {
                      setFamilyForm(prev => ({
                        ...prev,
                        member_client_ids: e.target.checked
                          ? [...prev.member_client_ids, c.client_id]
                          : prev.member_client_ids.filter(id => id !== c.client_id)
                      }));
                    }} />
                    <span className="text-sm">{c.client_name} <span className="text-gray-400">({c.client_id})</span></span>
                  </label>
                ))}
                {clients.filter(c => c.partner_code === (editingFamily ? editingFamily.partner_code : familyForm.partner_code) && c.client_id !== familyForm.head_client_id).length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">{familyForm.partner_code || editingFamily ? 'No other clients for this partner' : 'Select a partner first'}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowFamilyModal(false)}>Cancel</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163d32] text-white" data-testid="save-family-btn" onClick={async () => {
              if (!familyForm.family_name.trim()) { toast.error('Family name is required'); return; }
              if (!editingFamily && !familyForm.head_client_id) { toast.error('Select a family head'); return; }
              try {
                if (editingFamily) {
                  await axios.put(`${API_URL}/api/admin/families/${editingFamily.family_id}`, {
                    family_name: familyForm.family_name,
                    member_client_ids: familyForm.member_client_ids
                  }, getAuthHeaders());
                  toast.success('Family updated');
                } else {
                  await axios.post(`${API_URL}/api/admin/families`, {
                    family_name: familyForm.family_name,
                    head_client_id: familyForm.head_client_id,
                    member_client_ids: familyForm.member_client_ids
                  }, getAuthHeaders());
                  toast.success('Family created');
                }
                setShowFamilyModal(false);
                fetchData('families');
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save family'); }
            }}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Bond Holdings Transaction Details Dialog */}
      <Dialog open={showBhTxn} onOpenChange={setShowBhTxn}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E]">Transaction Details</DialogTitle>
            {bhTxnClient && <p className="text-sm text-[#64748b]">{bhTxnClient.client_name} — {bhTxnClient.bond_name} ({bhTxnClient.bond_isin})</p>}
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-[#1B4D3E]"><TrendingUp className="h-4 w-4" />Investment Transactions</h3>
              <Table><TableHeader><TableRow className="bg-[#E8F5E9]/50"><TableHead className="text-[#1B4D3E]">Date</TableHead><TableHead className="text-[#1B4D3E]">ID</TableHead><TableHead className="text-right text-[#1B4D3E]">Qty</TableHead><TableHead className="text-right text-[#1B4D3E]">Invested</TableHead><TableHead className="text-right text-[#1B4D3E]">Yield</TableHead></TableRow></TableHeader>
              <TableBody>{bhTxnData.investments.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.invested_value)}</TableCell><TableCell className="text-right text-[#D4AF37]">{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{bhTxnData.investments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-[#64748b]">No investment transactions</TableCell></TableRow>}</TableBody></Table>
            </div>
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-[#D4AF37]"><Download className="h-4 w-4" />Redemption Transactions</h3>
              <Table><TableHeader><TableRow className="bg-[#D4AF37]/10"><TableHead className="text-[#D4AF37]">Date</TableHead><TableHead className="text-[#D4AF37]">ID</TableHead><TableHead className="text-right text-[#D4AF37]">Qty</TableHead><TableHead className="text-right text-[#D4AF37]">Redemption Value</TableHead><TableHead className="text-right text-[#D4AF37]">Yield</TableHead></TableRow></TableHeader>
              <TableBody>{bhTxnData.redemptions.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.redemption_value)}</TableCell><TableCell className="text-right text-[#D4AF37]">{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{bhTxnData.redemptions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-[#64748b]">No redemption transactions</TableCell></TableRow>}</TableBody></Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Generate API Key Dialog */}
      <Dialog open={showNewKeyModal} onOpenChange={(open) => { setShowNewKeyModal(open); if (!open) setGeneratedKey(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E]"><KeyRound className="h-5 w-5 inline mr-2" />Generate Partner API Key</DialogTitle>
          </DialogHeader>
          {generatedKey ? (
            <div className="space-y-4 mt-2">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-green-800 mb-2">API Key Generated Successfully</p>
                <p className="text-xs text-green-700 mb-3">Copy this key now. It will not be shown again.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded border text-xs font-mono break-all" data-testid="generated-api-key">{generatedKey}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(generatedKey).catch(() => {}); toast.success('API key copied!'); }} data-testid="copy-api-key-btn">Copy</Button>
                </div>
              </div>
              <DialogFooter><Button onClick={() => { setShowNewKeyModal(false); setGeneratedKey(''); }}>Done</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Partner <span className="text-red-500">*</span></Label>
                <Select value={newKeyPartnerCode} onValueChange={setNewKeyPartnerCode}>
                  <SelectTrigger data-testid="api-key-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                  <SelectContent>{partners.map(p => <SelectItem key={p.partner_code} value={p.partner_code}>{p.partner_name} ({p.partner_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Label (optional)</Label>
                <Input value={newKeyLabel} onChange={(e) => setNewKeyLabel(e.target.value)} placeholder="e.g., Production API Key" data-testid="api-key-label" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewKeyModal(false)}>Cancel</Button>
                <Button className="bg-[#1B4D3E] text-white" onClick={handleGenerateApiKey} disabled={!newKeyPartnerCode} data-testid="confirm-generate-key">Generate</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* API Key Usage Analytics Modal */}
      <Dialog open={showAnalyticsModal} onOpenChange={setShowAnalyticsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="analytics-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E] flex items-center gap-2"><BarChart3 className="h-5 w-5" />API Key Usage Analytics</DialogTitle>
          </DialogHeader>
          {loadingAnalytics ? (
            <p className="text-center py-12 text-sm text-[#64748b]">Loading analytics…</p>
          ) : !analyticsData ? (
            <p className="text-center py-12 text-sm text-[#64748b]">No data available.</p>
          ) : (
            <div className="space-y-5 mt-2">
              {/* Header info */}
              <div className="bg-[#F8FAFC] border rounded-lg p-4">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[#1B4D3E]">{analyticsData.partner_name || analyticsData.partner_code}</p>
                    <p className="text-xs text-[#64748b] mt-0.5">{analyticsData.label}</p>
                  </div>
                  <div className="text-right">
                    {analyticsData.is_active
                      ? <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Active</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-600">Inactive</span>}
                    <p className="text-xs text-[#64748b] mt-1">Created {formatDate(analyticsData.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* KPI tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-[#64748b]">All-Time Requests</p><p className="text-xl font-bold text-[#1B4D3E]" data-testid="ak-total">{(analyticsData.total_requests || 0).toLocaleString()}</p></div>
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-[#64748b]">Last {analyticsData.window_days} Days</p><p className="text-xl font-bold text-[#0369A1]" data-testid="ak-window">{(analyticsData.window_total || 0).toLocaleString()}</p></div>
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-[#64748b]">Last Used</p><p className="text-sm font-semibold text-[#1B4D3E] mt-1">{analyticsData.last_used ? formatDate(analyticsData.last_used) : 'Never'}</p></div>
                <div className="bg-white border rounded-lg p-3"><p className="text-xs text-[#64748b]">Rate Limit</p><p className="text-sm font-semibold text-[#B8860B] mt-1">100 / min</p></div>
              </div>

              {/* Range selector */}
              <div className="flex items-center gap-2">
                <p className="text-xs text-[#64748b]">Time range:</p>
                {[7, 14, 30, 90].map(d => (
                  <Button key={d} size="sm" variant={analyticsRange === d ? 'default' : 'outline'} className={analyticsRange === d ? 'bg-[#1B4D3E] text-white h-7 text-xs' : 'h-7 text-xs'} onClick={() => openAnalytics(analyticsData.key_id, d)} data-testid={`range-${d}d`}>{d}d</Button>
                ))}
              </div>

              {/* Chart */}
              <div className="bg-white border rounded-lg p-3">
                <p className="text-sm font-semibold text-[#1B4D3E] mb-2">Daily Requests · Last {analyticsData.window_days} Days</p>
                {analyticsData.window_total === 0 ? (
                  <p className="text-center py-12 text-sm text-[#64748b]">No requests recorded in this window.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={analyticsData.series} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} interval={analyticsData.series.length > 30 ? 6 : 'preserveStartEnd'} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <ReTooltip formatter={(value) => [`${value} req`, 'Requests']} labelFormatter={(label) => formatDate(label)} />
                      <Bar dataKey="requests" fill="#1B4D3E" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowAnalyticsModal(false)} data-testid="close-analytics">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Login URL — QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="max-w-md" data-testid="qr-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1B4D3E] flex items-center gap-2"><QrCode className="h-5 w-5" />Scan to open {qrPartnerName ? `${qrPartnerName} portal` : 'portal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#64748b]">Partners and clients can scan this QR with their phone camera to open the branded login page and "Add to Home Screen".</p>
            <div className="bg-white border-2 border-[#E8F5E9] rounded-lg p-6 flex items-center justify-center" data-testid="qr-canvas-wrapper">
              <QRCodeCanvas
                id="custom-login-qr-canvas"
                value={qrUrl}
                size={260}
                level="H"
                includeMargin={true}
                fgColor="#1B4D3E"
                bgColor="#ffffff"
                imageSettings={(qrShowLogo && qrLogoUrl) ? {
                  src: qrLogoUrl,
                  height: 56,
                  width: 56,
                  excavate: true,
                  crossOrigin: 'anonymous',
                } : undefined}
              />
            </div>
            {qrLogoUrl && (
              <div className="flex items-center gap-2 text-xs">
                <input type="checkbox" id="qr-show-logo" checked={qrShowLogo} onChange={(e) => setQrShowLogo(e.target.checked)} className="h-3.5 w-3.5 accent-[#1B4D3E]" data-testid="qr-show-logo-toggle" />
                <Label htmlFor="qr-show-logo" className="cursor-pointer text-[#64748b]">Include partner logo in centre <span className="text-[#94a3b8]">(uncheck if download fails due to logo host CORS)</span></Label>
              </div>
            )}
            <div className="bg-slate-50 rounded-md px-3 py-2 flex items-center gap-2">
              <code className="flex-1 text-xs font-mono text-[#1B4D3E] break-all" data-testid="qr-url-text">{qrUrl}</code>
              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs shrink-0" onClick={() => { navigator.clipboard?.writeText(qrUrl).then(() => toast.success('URL copied')).catch(() => toast.error('Copy failed')); }} data-testid="qr-copy-url"><Check className="h-3 w-3 mr-1" />Copy</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="text-xs" onClick={() => {
                const canvas = document.getElementById('custom-login-qr-canvas');
                if (!canvas) { toast.error('QR not ready'); return; }
                try {
                  const dataUrl = canvas.toDataURL('image/png');
                  const link = document.createElement('a');
                  const safeName = (qrPartnerName || 'partner').toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  link.download = `${safeName}-login-qr.png`;
                  link.href = dataUrl;
                  link.click();
                  toast.success('QR downloaded');
                } catch (e) {
                  if (e && (e.name === 'SecurityError' || /tainted/i.test(e.message || ''))) {
                    toast.error("Logo host blocks image export. Uncheck 'Include partner logo' and try again.");
                  } else { toast.error('Download failed'); }
                }
              }} data-testid="qr-download-btn"><FileDown className="h-3 w-3 mr-1" />Download PNG</Button>
              <Button type="button" variant="outline" className="text-xs" onClick={async () => {
                const canvas = document.getElementById('custom-login-qr-canvas');
                if (!canvas) { toast.error('QR not ready'); return; }
                try {
                  const blob = await new Promise((res, rej) => {
                    try { canvas.toBlob((b) => b ? res(b) : rej(new Error('null blob')), 'image/png'); }
                    catch (err) { rej(err); }
                  });
                  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                  toast.success('QR copied to clipboard');
                } catch (e) {
                  if (e && (e.name === 'SecurityError' || /tainted/i.test(e.message || ''))) {
                    toast.error("Logo host blocks image copy. Uncheck 'Include partner logo' and try again.");
                  } else { toast.error('Image-copy unsupported. Use Download instead.'); }
                }
              }} data-testid="qr-copy-image-btn"><ImageIcon className="h-3 w-3 mr-1" />Copy Image</Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowQrModal(false)} data-testid="close-qr-modal">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password confirmation for destructive actions */}
      <PasswordConfirmModal
        open={pwdConfirm.open}
        onOpenChange={(o) => setPwdConfirm(prev => ({ ...prev, open: o }))}
        title={pwdConfirm.title}
        description={pwdConfirm.description}
        itemLabel={pwdConfirm.itemLabel}
        getAuthHeaders={getAuthHeaders}
        onConfirmed={pwdConfirm.onConfirmed || (() => {})}
      />
    </div>
  );
};

export default AdminDashboard;
